function getFocusableElements(container) {
    return Array.from(
        container.querySelectorAll(
            "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
        )
    );
}

class SectionId {
    static #separator = '__';

    // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
    static parseId(qualifiedSectionId) {
        return qualifiedSectionId.split(SectionId.#separator)[0];
    }

    // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
    static parseSectionName(qualifiedSectionId) {
        return qualifiedSectionId.split(SectionId.#separator)[1];
    }

    // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
    static getIdForSection(sectionId, sectionName) {
        return `${sectionId}${SectionId.#separator}${sectionName}`;
    }
}

class HTMLUpdateUtility {
    /**
     * Used to swap an HTML node with a new node.
     * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
     *
     * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
     */
    static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
        preProcessCallbacks?.forEach((callback) => callback(newContent));

        const newNodeWrapper = document.createElement('div');
        HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
        const newNode = newNodeWrapper.firstChild;

        // dedupe IDs
        const uniqueKey = Date.now();
        oldNode.querySelectorAll('[id], [form]').forEach((element) => {
            element.id && (element.id = `${element.id}-${uniqueKey}`);
            element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
        });

        oldNode.parentNode.insertBefore(newNode, oldNode);
        oldNode.style.display = 'none';

        postProcessCallbacks?.forEach((callback) => callback(newNode));

        setTimeout(() => oldNode.remove(), 500);
    }

    // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
    static setInnerHTML(element, html) {
        element.innerHTML = html;
        element.querySelectorAll('script').forEach((oldScriptTag) => {
            const newScriptTag = document.createElement('script');
            Array.from(oldScriptTag.attributes).forEach((attribute) => {
                newScriptTag.setAttribute(attribute.name, attribute.value);
            });
            newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
            oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
        });
    }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
    summary.setAttribute('role', 'button');
    summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

    if (summary.nextElementSibling.getAttribute('id')) {
        summary.setAttribute('aria-controls', summary.nextElementSibling.id);
    }

    summary.addEventListener('click', (event) => {
        event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    if (summary.closest('header-drawer, menu-drawer')) return;
    summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
    var elements = getFocusableElements(container);
    var first = elements[0];
    var last = elements[elements.length - 1];

    removeTrapFocus();

    trapFocusHandlers.focusin = (event) => {
        if (event.target !== container && event.target !== last && event.target !== first) return;

        document.addEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.focusout = function () {
        document.removeEventListener('keydown', trapFocusHandlers.keydown);
    };

    trapFocusHandlers.keydown = function (event) {
        if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
        // On the last focusable element and tab forward, focus the first element.
        if (event.target === last && !event.shiftKey) {
            event.preventDefault();
            first.focus();
        }

        //  On the first focusable element and tab backward, focus the last element.
        if ((event.target === container || event.target === first) && event.shiftKey) {
            event.preventDefault();
            last.focus();
        }
    };

    document.addEventListener('focusout', trapFocusHandlers.focusout);
    document.addEventListener('focusin', trapFocusHandlers.focusin);

    elementToFocus.focus();

    if (
        elementToFocus.tagName === 'INPUT' &&
        ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
        elementToFocus.value
    ) {
        elementToFocus.setSelectionRange(0, elementToFocus.value.length);
    }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
    document.querySelector(':focus-visible');
} catch (e) {
    focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
    const navKeys = [
        'ARROWUP',
        'ARROWDOWN',
        'ARROWLEFT',
        'ARROWRIGHT',
        'TAB',
        'ENTER',
        'SPACE',
        'ESCAPE',
        'HOME',
        'END',
        'PAGEUP',
        'PAGEDOWN',
    ];
    let currentFocusedElement = null;
    let mouseClick = null;

    window.addEventListener('keydown', (event) => {
        if (navKeys.includes(event.code.toUpperCase())) {
            mouseClick = false;
        }
    });

    window.addEventListener('mousedown', (event) => {
        mouseClick = true;
    });

    window.addEventListener(
        'focus',
        () => {
            if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

            if (mouseClick) return;

            currentFocusedElement = document.activeElement;
            currentFocusedElement.classList.add('focused');
        },
        true
    );
}

function pauseAllMedia() {
    document.querySelectorAll('.js-youtube').forEach((video) => {
        video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
    });
    document.querySelectorAll('.js-vimeo').forEach((video) => {
        video.contentWindow.postMessage('{"method":"pause"}', '*');
    });
    document.querySelectorAll('video').forEach((video) => video.pause());
    document.querySelectorAll('product-model').forEach((model) => {
        if (model.modelViewerUI) model.modelViewerUI.pause();
    });
}

function removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', trapFocusHandlers.focusin);
    document.removeEventListener('focusout', trapFocusHandlers.focusout);
    document.removeEventListener('keydown', trapFocusHandlers.keydown);

    if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    const summaryElement = openDetailsElement.querySelector('summary');
    openDetailsElement.removeAttribute('open');
    summaryElement.setAttribute('aria-expanded', false);
    summaryElement.focus();
}

class QuantityInput extends HTMLElement {
    constructor() {
        super();
        this.input = this.querySelector('input');
        this.changeEvent = new Event('change', { bubbles: true });
        this.input.addEventListener('change', this.onInputChange.bind(this));
        this.querySelectorAll('button').forEach((button) =>
            button.addEventListener('click', this.onButtonClick.bind(this))
        );
    }

    quantityUpdateUnsubscriber = undefined;

    connectedCallback() {
        this.validateQtyRules();
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
    }

    disconnectedCallback() {
        if (this.quantityUpdateUnsubscriber) {
            this.quantityUpdateUnsubscriber();
        }
    }

    onInputChange(event) {
        this.validateQtyRules();
    }

    onButtonClick(event) {
        event.preventDefault();
        const previousValue = this.input.value;

        if (event.target.name === 'plus') {
            if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
                this.input.value = this.input.dataset.min;
            } else {
                this.input.stepUp();
            }
        } else {
            this.input.stepDown();
        }

        if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

        if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
            this.input.value = parseInt(this.input.min);
        }
    }

    validateQtyRules() {
        const value = parseInt(this.input.value);
        if (this.input.min) {
            const buttonMinus = this.querySelector(".quantity__button[name='minus']");
            buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
        }
        if (this.input.max) {
            const max = parseInt(this.input.max);
            const buttonPlus = this.querySelector(".quantity__button[name='plus']");
            buttonPlus.classList.toggle('disabled', value >= max);
        }
    }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
    let t;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(this, args), wait);
    };
}

function throttle(fn, delay) {
    let lastCall = 0;
    return function (...args) {
        const now = new Date().getTime();
        if (now - lastCall < delay) {
            return;
        }
        lastCall = now;
        return fn(...args);
    };
}

function fetchConfig(type = 'json') {
    return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
    window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
    return function () {
        return fn.apply(scope, arguments);
    };
};

Shopify.setSelectorByValue = function (selector, value) {
    for (var i = 0, count = selector.options.length; i < count; i++) {
        var option = selector.options[i];
        if (value == option.value || value == option.innerHTML) {
            selector.selectedIndex = i;
            return i;
        }
    }
};

Shopify.addListener = function (target, eventName, callback) {
    target.addEventListener
        ? target.addEventListener(eventName, callback, false)
        : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
    options = options || {};
    var method = options['method'] || 'post';
    var params = options['parameters'] || {};

    var form = document.createElement('form');
    form.setAttribute('method', method);
    form.setAttribute('action', path);

    for (var key in params) {
        var hiddenField = document.createElement('input');
        hiddenField.setAttribute('type', 'hidden');
        hiddenField.setAttribute('name', key);
        hiddenField.setAttribute('value', params[key]);
        form.appendChild(hiddenField);
    }
    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
    this.countryEl = document.getElementById(country_domid);
    this.provinceEl = document.getElementById(province_domid);
    this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

    Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

    this.initCountry();
    this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
    initCountry: function () {
        var value = this.countryEl.getAttribute('data-default');
        Shopify.setSelectorByValue(this.countryEl, value);
        this.countryHandler();
    },

    initProvince: function () {
        var value = this.provinceEl.getAttribute('data-default');
        if (value && this.provinceEl.options.length > 0) {
            Shopify.setSelectorByValue(this.provinceEl, value);
        }
    },

    countryHandler: function (e) {
        var opt = this.countryEl.options[this.countryEl.selectedIndex];
        var raw = opt.getAttribute('data-provinces');
        var provinces = JSON.parse(raw);

        this.clearOptions(this.provinceEl);
        if (provinces && provinces.length == 0) {
            this.provinceContainer.style.display = 'none';
        } else {
            for (var i = 0; i < provinces.length; i++) {
                var opt = document.createElement('option');
                opt.value = provinces[i][0];
                opt.innerHTML = provinces[i][1];
                this.provinceEl.appendChild(opt);
            }

            this.provinceContainer.style.display = '';
        }
    },

    clearOptions: function (selector) {
        while (selector.firstChild) {
            selector.removeChild(selector.firstChild);
        }
    },

    setOptions: function (selector, values) {
        for (var i = 0, count = values.length; i < values.length; i++) {
            var opt = document.createElement('option');
            opt.value = values[i];
            opt.innerHTML = values[i];
            selector.appendChild(opt);
        }
    },
};

class MenuDrawer extends HTMLElement {
    constructor() {
        super();

        this.mainDetailsToggle = this.querySelector('details');

        this.addEventListener('keyup', this.onKeyUp.bind(this));
        this.addEventListener('focusout', this.onFocusOut.bind(this));
        this.bindEvents();
    }

    bindEvents() {
        this.querySelectorAll('summary').forEach((summary) => {
            if (summary.dataset.menuDrawerBound === 'true') return;
            summary.dataset.menuDrawerBound = 'true';
            summary.addEventListener('click', this.onSummaryClick.bind(this));
        });
        this.querySelectorAll(
            'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
        ).forEach((button) => {
            if (button.dataset.menuDrawerBound === 'true') return;
            button.dataset.menuDrawerBound = 'true';
            button.addEventListener('click', this.onCloseButtonClick.bind(this));
        });
    }

    onKeyUp(event) {
        if (event.code.toUpperCase() !== 'ESCAPE') return;

        const openDetailsElement = event.target.closest('details[open]');
        if (!openDetailsElement) return;

        openDetailsElement === this.mainDetailsToggle
            ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
            : this.closeSubmenu(openDetailsElement);
    }

    onSummaryClick(event) {
        const summaryElement = event.currentTarget;
        const detailsElement = summaryElement.parentNode;
        const parentMenuElement = detailsElement.closest('.has-submenu');
        const isOpen = detailsElement.hasAttribute('open');
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        function addTrapFocus() {
            trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
            summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
        }

        if (detailsElement === this.mainDetailsToggle) {
            if (isOpen) event.preventDefault();
            isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

            if (window.matchMedia('(max-width: 990px)')) {
                document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
            }
        } else {
            setTimeout(() => {
                detailsElement.classList.add('menu-opening');
                summaryElement.setAttribute('aria-expanded', true);
                parentMenuElement && parentMenuElement.classList.add('submenu-open');
                !reducedMotion || reducedMotion.matches
                    ? addTrapFocus()
                    : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
            }, 100);
        }
    }

    openMenuDrawer(summaryElement) {
        setTimeout(() => {
            this.mainDetailsToggle.classList.add('menu-opening');
        });
        summaryElement.setAttribute('aria-expanded', true);
        trapFocus(this.mainDetailsToggle, summaryElement);
        document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    }

    closeMenuDrawer(event, elementToFocus = false) {
        if (event === undefined) return;

        this.mainDetailsToggle.classList.remove('menu-opening');
        this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
            details.removeAttribute('open');
            details.classList.remove('menu-opening');
        });
        this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
            submenu.classList.remove('submenu-open');
        });
        document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
        removeTrapFocus(elementToFocus);
        this.closeAnimation(this.mainDetailsToggle);

        if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
    }

    onFocusOut() {
        setTimeout(() => {
            if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
                this.closeMenuDrawer();
        });
    }

    onCloseButtonClick(event) {
        const detailsElement = event.currentTarget.closest('details');
        this.closeSubmenu(detailsElement);
    }

    closeSubmenu(detailsElement) {
        const parentMenuElement = detailsElement.closest('.submenu-open');
        parentMenuElement && parentMenuElement.classList.remove('submenu-open');
        detailsElement.classList.remove('menu-opening');
        detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
        removeTrapFocus(detailsElement.querySelector('summary'));
        this.closeAnimation(detailsElement);
    }

    closeAnimation(detailsElement) {
        let animationStart;

        const handleAnimation = (time) => {
            if (animationStart === undefined) {
                animationStart = time;
            }

            const elapsedTime = time - animationStart;

            if (elapsedTime < 400) {
                window.requestAnimationFrame(handleAnimation);
            } else {
                detailsElement.removeAttribute('open');
                if (detailsElement.closest('details[open]')) {
                    trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
                }
            }
        };

        window.requestAnimationFrame(handleAnimation);
    }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
    constructor() {
        super();
    }

    openMenuDrawer(summaryElement) {
        this.header = this.header || this.closest('.section-header') || document.querySelector('.section-header');
        const headerWrapper = this.closest('.header-wrapper');
        this.borderOffset = headerWrapper?.classList.contains('header-wrapper--border-bottom') ? 1 : 0;
        document.documentElement.style.setProperty(
            '--header-bottom-position',
            `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
        );
        this.header?.classList.add('menu-open');

        setTimeout(() => {
            this.mainDetailsToggle.classList.add('menu-opening');
        });

        summaryElement.setAttribute('aria-expanded', true);
        window.addEventListener('resize', this.onResize);
        trapFocus(this.mainDetailsToggle, summaryElement);
        document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
    }

    closeMenuDrawer(event, elementToFocus) {
        if (!elementToFocus) return;
        super.closeMenuDrawer(event, elementToFocus);
        this.header?.classList.remove('menu-open');
        window.removeEventListener('resize', this.onResize);
    }

    onResize = () => {
        this.header &&
        document.documentElement.style.setProperty(
            '--header-bottom-position',
            `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
        );
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
    constructor() {
        super();

        this.querySelectorAll('[id^="ModalClose-"]').forEach((closeBtn) => {
            closeBtn.addEventListener('click', this.hide.bind(this, false));
        });
        this.querySelectorAll('[data-close-id^="ModalClose-"]').forEach((closeBtn) => {
            closeBtn.addEventListener('click', this.hide.bind(this, false));
        });
        this.addEventListener('keyup', (event) => {
            if (event.code.toUpperCase() === 'ESCAPE') this.hide();
        });
        if (this.classList.contains('media-modal')) {
            this.addEventListener('pointerup', (event) => {
                if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
            });
        } else {
            this.addEventListener('click', (event) => {
                if (event.target === this) this.hide();
                if(event.target.classList.contains('modal-content')) {
                    this.hide();
                }
            });
        }
    }

    connectedCallback() {
        if (this.moved) return;
        this.moved = true;
        this.dataset.section = this.closest('.shopify-section').id.replace('shopify-section-', '');
        document.body.appendChild(this);
    }

    show(opener) {
        this.openedBy = opener;
        const popup = this.querySelector('.template-popup');
        document.body.classList.add('overflow-hidden');
        this.setAttribute('open', '');
        if (popup) popup.loadContent();
        trapFocus(this, this.querySelector('[role="dialog"]'));
        window.pauseAllMedia();
    }

    hide() {
        document.body.classList.remove('overflow-hidden');
        document.body.dispatchEvent(new CustomEvent('modalClosed'));
        this.removeAttribute('open');
        removeTrapFocus(this.openedBy);
        window.pauseAllMedia();
    }
}
customElements.define('custom-modal-dialog', ModalDialog);

class BulkModal extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        const handleIntersection = (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.unobserve(this);
            if (this.innerHTML.trim() === '') {
                const productUrl = this.dataset.url.split('?')[0];
                fetch(`${productUrl}?section_id=bulk-quick-order-list`)
                    .then((response) => response.text())
                    .then((responseText) => {
                        const html = new DOMParser().parseFromString(responseText, 'text/html');
                        const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
                        this.innerHTML = sourceQty.innerHTML;
                    })
                    .catch((e) => {
                        console.error(e);
                    });
            }
        };

        new IntersectionObserver(handleIntersection.bind(this)).observe(
            document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
        );
    }
}

customElements.define('bulk-modal', BulkModal);

class ModalOpener extends HTMLElement {
    constructor() {
        super();

        const button = this.querySelector('button, [data-modal-opener]');

        if (!button) return;
        button.addEventListener('click', () => {
            const modal = document.querySelector(this.getAttribute('data-modal'));
            if (modal) modal.show(button);
        });
    }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
    constructor() {
        super();
        const poster = this.querySelector('[id^="Deferred-Poster-"]');
        if (!poster) return;
        poster.addEventListener('click', this.loadContent.bind(this));
    }

    loadContent(focus = true) {
        window.pauseAllMedia();
        if (!this.getAttribute('loaded')) {
            const content = document.createElement('div');
            content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

            this.setAttribute('loaded', true);
            const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
            if (focus) deferredElement.focus();
            if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
                // force autoplay for safari
                deferredElement.play();
            }
        }
    }
}

customElements.define('deferred-media', DeferredMedia);

class SliderComponent extends HTMLElement {
    constructor() {
        super();
        this.slider = this.querySelector('[id^="Slider-"]');
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.enableSliderLooping = false;
        this.currentPageElement = this.querySelector('.slider-counter--current');
        this.pageTotalElement = this.querySelector('.slider-counter--total');
        this.prevButton = this.querySelector('button[name="previous"]');
        this.nextButton = this.querySelector('button[name="next"]');

        if (!this.slider || !this.nextButton) return;

        this.initPages();
        const resizeObserver = new ResizeObserver((entries) => this.initPages());
        resizeObserver.observe(this.slider);

        this.slider.addEventListener('scroll', this.update.bind(this));
        this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
        this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
    }

    initPages() {
        this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
        if (this.sliderItemsToShow.length < 2) return;
        this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
        this.slidesPerPage = Math.floor(
            (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
        );
        this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
        this.update();
    }

    resetPages() {
        this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
        this.initPages();
    }

    update() {
        // Temporarily prevents unneeded updates resulting from variant changes
        // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
        if (!this.slider || !this.nextButton) return;

        const previousPage = this.currentPage;
        this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

        if (this.currentPageElement && this.pageTotalElement) {
            this.currentPageElement.textContent = this.currentPage;
            this.pageTotalElement.textContent = this.totalPages;
        }

        if (this.currentPage != previousPage) {
            this.dispatchEvent(
                new CustomEvent('slideChanged', {
                    detail: {
                        currentPage: this.currentPage,
                        currentElement: this.sliderItemsToShow[this.currentPage - 1],
                    },
                })
            );
        }

        if (this.enableSliderLooping) return;

        if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
            this.prevButton.setAttribute('disabled', 'disabled');
        } else {
            this.prevButton.removeAttribute('disabled');
        }

        if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
            this.nextButton.setAttribute('disabled', 'disabled');
        } else {
            this.nextButton.removeAttribute('disabled');
        }
    }

    isSlideVisible(element, offset = 0) {
        const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
        return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
    }

    onButtonClick(event) {
        event.preventDefault();
        const step = event.currentTarget.dataset.step || 1;
        this.slideScrollPosition =
            event.currentTarget.name === 'next'
                ? this.slider.scrollLeft + step * this.sliderItemOffset
                : this.slider.scrollLeft - step * this.sliderItemOffset;
        this.setSlidePosition(this.slideScrollPosition);
    }

    setSlidePosition(position) {
        this.slider.scrollTo({
            left: position,
        });
    }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
    constructor() {
        super();
        this.sliderControlWrapper = this.querySelector('.slider-buttons');
        this.enableSliderLooping = true;

        if (!this.sliderControlWrapper) return;

        this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
        if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

        this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
        // Value below should match --duration-announcement-bar CSS value
        this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

        this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
        this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
        this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
        this.setSlideVisibility();

        if (this.announcementBarSlider) {
            this.announcementBarArrowButtonWasClicked = false;

            this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
            this.reducedMotion.addEventListener('change', () => {
                if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
            });

            [this.prevButton, this.nextButton].forEach((button) => {
                button.addEventListener(
                    'click',
                    () => {
                        this.announcementBarArrowButtonWasClicked = true;
                    },
                    { once: true }
                );
            });
        }

        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
    }

    setAutoPlay() {
        this.autoplaySpeed = this.slider.dataset.speed * 1000;
        this.addEventListener('mouseover', this.focusInHandling.bind(this));
        this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
        this.addEventListener('focusin', this.focusInHandling.bind(this));
        this.addEventListener('focusout', this.focusOutHandling.bind(this));

        if (this.querySelector('.slideshow__autoplay')) {
            this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
            this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
            this.autoplayButtonIsSetToPlay = true;
            this.play();
        } else {
            this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
        }
    }

    onButtonClick(event) {
        super.onButtonClick(event);
        this.wasClicked = true;

        const isFirstSlide = this.currentPage === 1;
        const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

        if (!isFirstSlide && !isLastSlide) {
            this.applyAnimationToAnnouncementBar(event.currentTarget.name);
            return;
        }

        if (isFirstSlide && event.currentTarget.name === 'previous') {
            this.slideScrollPosition =
                this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
        } else if (isLastSlide && event.currentTarget.name === 'next') {
            this.slideScrollPosition = 0;
        }

        this.setSlidePosition(this.slideScrollPosition);

        this.applyAnimationToAnnouncementBar(event.currentTarget.name);
    }

    setSlidePosition(position) {
        if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
        this.setPositionTimeout = setTimeout(() => {
            this.slider.scrollTo({
                left: position,
            });
        }, this.announcerBarAnimationDelay);
    }

    update() {
        super.update();
        this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
        this.prevButton.removeAttribute('disabled');

        if (!this.sliderControlButtons.length) return;

        this.sliderControlButtons.forEach((link) => {
            link.classList.remove('slider-counter__link--active');
            link.removeAttribute('aria-current');
        });
        this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
        this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
    }

    autoPlayToggle() {
        this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
        this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
        this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
    }

    focusOutHandling(event) {
        if (this.sliderAutoplayButton) {
            const focusedOnAutoplayButton =
                event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
            if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
            this.play();
        } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
            this.play();
        }
    }

    focusInHandling(event) {
        if (this.sliderAutoplayButton) {
            const focusedOnAutoplayButton =
                event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
            if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
                this.play();
            } else if (this.autoplayButtonIsSetToPlay) {
                this.pause();
            }
        } else if (this.announcementBarSlider.contains(event.target)) {
            this.pause();
        }
    }

    play() {
        this.slider.setAttribute('aria-live', 'off');
        clearInterval(this.autoplay);
        this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
    }

    pause() {
        this.slider.setAttribute('aria-live', 'polite');
        clearInterval(this.autoplay);
    }

    togglePlayButtonState(pauseAutoplay) {
        if (pauseAutoplay) {
            this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
            this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
        } else {
            this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
            this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
        }
    }

    autoRotateSlides() {
        const slideScrollPosition =
            this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

        this.setSlidePosition(slideScrollPosition);
        this.applyAnimationToAnnouncementBar();
    }

    setSlideVisibility(event) {
        this.sliderItemsToShow.forEach((item, index) => {
            const linkElements = item.querySelectorAll('a');
            if (index === this.currentPage - 1) {
                if (linkElements.length)
                    linkElements.forEach((button) => {
                        button.removeAttribute('tabindex');
                    });
                item.setAttribute('aria-hidden', 'false');
                item.removeAttribute('tabindex');
            } else {
                if (linkElements.length)
                    linkElements.forEach((button) => {
                        button.setAttribute('tabindex', '-1');
                    });
                item.setAttribute('aria-hidden', 'true');
                item.setAttribute('tabindex', '-1');
            }
        });
        this.wasClicked = false;
    }

    applyAnimationToAnnouncementBar(button = 'next') {
        if (!this.announcementBarSlider) return;

        const itemsCount = this.sliderItems.length;
        const increment = button === 'next' ? 1 : -1;

        const currentIndex = this.currentPage - 1;
        let nextIndex = (currentIndex + increment) % itemsCount;
        nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

        const nextSlide = this.sliderItems[nextIndex];
        const currentSlide = this.sliderItems[currentIndex];

        const animationClassIn = 'announcement-bar-slider--fade-in';
        const animationClassOut = 'announcement-bar-slider--fade-out';

        const isFirstSlide = currentIndex === 0;
        const isLastSlide = currentIndex === itemsCount - 1;

        const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
        const direction = shouldMoveNext ? 'next' : 'previous';

        currentSlide.classList.add(`${animationClassOut}-${direction}`);
        nextSlide.classList.add(`${animationClassIn}-${direction}`);

        setTimeout(() => {
            currentSlide.classList.remove(`${animationClassOut}-${direction}`);
            nextSlide.classList.remove(`${animationClassIn}-${direction}`);
        }, this.announcerBarAnimationDelay * 2);
    }

    linkToSlide(event) {
        event.preventDefault();
        const slideScrollPosition =
            this.slider.scrollLeft +
            this.sliderFirstItemNode.clientWidth *
            (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
        this.slider.scrollTo({
            left: slideScrollPosition,
        });
    }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
    constructor() {
        super();
    }

    connectedCallback() {
        this.addEventListener('change', (event) => {
            const target = this.getInputForEventTarget(event.target);
            this.updateSelectionMetadata(event);

            publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
                data: {
                    event,
                    target,
                    selectedOptionValues: this.selectedOptionValues,
                },
            });
        });
    }

    updateSelectionMetadata({ target }) {
        const { value, tagName } = target;

        if (tagName === 'SELECT' && target.selectedOptions.length) {
            Array.from(target.options)
                .find((option) => option.getAttribute('selected'))
                .removeAttribute('selected');
            target.selectedOptions[0].setAttribute('selected', 'selected');

            const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
            const selectedDropdownSwatchValue = target
                .closest('.product-form__input')
                .querySelector('[data-selected-value] > .swatch');
            if (!selectedDropdownSwatchValue) return;
            if (swatchValue) {
                selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
                selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
            } else {
                selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
                selectedDropdownSwatchValue.classList.add('swatch--unavailable');
            }

            selectedDropdownSwatchValue.style.setProperty(
                '--swatch-focal-point',
                target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
            );
        } else if (tagName === 'INPUT' && target.type === 'radio') {
            const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
            if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
    }

    getInputForEventTarget(target) {
        return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
    }

    get selectedOptionValues() {
        return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
            ({ dataset }) => dataset.optionValueId
        );
    }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
    observer = undefined;

    constructor() {
        super();
    }

    connectedCallback() {
        this.initializeRecommendations(this.dataset.productId);
    }

    initializeRecommendations(productId) {
        this.observer?.unobserve(this);
        this.observer = new IntersectionObserver(
            (entries, observer) => {
                if (!entries[0].isIntersecting) return;
                observer.unobserve(this);
                this.loadRecommendations(productId);
            },
            { rootMargin: '0px 0px 400px 0px' }
        );
        this.observer.observe(this);
    }

    loadRecommendations(productId) {
        fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
            .then((response) => response.text())
            .then((text) => {
                const html = document.createElement('div');
                html.innerHTML = text;
                const recommendations = html.querySelector('product-recommendations');

                if (recommendations?.innerHTML.trim().length) {
                    this.innerHTML = recommendations.innerHTML;
                }

                if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
                    this.remove();
                }

                if (html.querySelector('.grid__item')) {
                    this.classList.add('product-recommendations--loaded');
                }
            })
            .catch((e) => {
                console.error(e);
            });
    }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
    constructor() {
        super();

        this.icon = this.querySelector('.icon');
    }

    connectedCallback() {
        document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
    }

    handleStorefrontSignInCompleted(event) {
        if (event?.detail?.avatar) {
            this.icon?.replaceWith(event.detail.avatar.cloneNode());
        }
    }
}

customElements.define('account-icon', AccountIcon);

class BulkAdd extends HTMLElement {
    constructor() {
        super();
        this.queue = [];
        this.requestStarted = false;
        this.ids = [];
    }

    startQueue(id, quantity) {
        this.queue.push({ id, quantity });
        const interval = setInterval(() => {
            if (this.queue.length > 0) {
                if (!this.requestStarted) {
                    this.sendRequest(this.queue);
                }
            } else {
                clearInterval(interval);
            }
        }, 250);
    }

    sendRequest(queue) {
        this.requestStarted = true;
        const items = {};
        queue.forEach((queueItem) => {
            items[parseInt(queueItem.id)] = queueItem.quantity;
        });
        this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
        const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
        quickBulkElement.updateMultipleQty(items);
    }

    resetQuantityInput(id) {
        const input = this.querySelector(`#Quantity-${id}`);
        input.value = input.getAttribute('value');
        this.isEnterPressed = false;
    }

    setValidity(event, index, message) {
        event.target.setCustomValidity(message);
        event.target.reportValidity();
        this.resetQuantityInput(index);
        event.target.select();
    }

    validateQuantity(event) {
        const inputValue = parseInt(event.target.value);
        const index = event.target.dataset.index;

        if (inputValue < event.target.dataset.min) {
            this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
        } else if (inputValue > parseInt(event.target.max)) {
            this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
        } else if (inputValue % parseInt(event.target.step) != 0) {
            this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
        } else {
            event.target.setCustomValidity('');
            event.target.reportValidity();
            this.startQueue(index, inputValue);
        }
    }

    getSectionsUrl() {
        if (window.pageNumber) {
            return `${window.location.pathname}?page=${window.pageNumber}`;
        } else {
            return `${window.location.pathname}`;
        }
    }

    getSectionInnerHTML(html, selector) {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
    }
}

if (!customElements.get('bulk-add')) {
    customElements.define('bulk-add', BulkAdd);
}

class ReadMore extends HTMLElement {
    constructor() {
        super();
        this.sortDescription = this.querySelector('[data-sort-description]');
        this.fullDescription = this.querySelector('[data-full-description]');
        this.readMoreBtn = this.querySelector('[data-read-more-btn]');
        this.readLessBtn = this.querySelector('[data-read-less-btn]');

        this.init();
    }

    init() {
        if (this.readMoreBtn) {
            this.readMoreBtn.addEventListener('click', () => this.showFullDescription());
        }

        if (this.readLessBtn) {
            this.readLessBtn.addEventListener('click', () => this.showSortDescription());
        }
    }

    showFullDescription() {
        this.sortDescription.classList.add('d-none');
        this.fullDescription.classList.remove('d-none');
    }

    showSortDescription() {
        this.fullDescription.classList.add('d-none');
        this.sortDescription.classList.remove('d-none');
    }
}

customElements.define('custom-read-more', ReadMore);

class AnnouncementBar extends HTMLElement {
    constructor() {
        super();
        this.announcementBar = this.querySelector('.announcement-bar');
        this.announcementBarBtn = this.querySelector('.announcement-bar-close-btn');
        this.init();
    }

    init() {
        if (this.announcementBarBtn) {
            this.announcementBarBtn.addEventListener('click', () => this.hideAnnouncementBar());
        }
    }

    hideAnnouncementBar() {
        this.announcementBar.classList.add('d-none');
    }
}

customElements.define('announcement-bar', AnnouncementBar);

class SwatchScroller extends HTMLElement {
    constructor() {
        super();
        this.track = this.querySelector('[data-swatch-scroll-track]');
        this.prevButton = this.querySelector('[data-swatch-scroll-prev]');
        this.nextButton = this.querySelector('[data-swatch-scroll-next]');
        this.handleUpdate = this.updateState.bind(this);
        this.handleDeferredUpdate = () => requestAnimationFrame(() => this.updateState());
        this.handlePrevClick = this.scrollPrev.bind(this);
        this.handleNextClick = this.scrollNext.bind(this);
    }

    connectedCallback() {
        if (!this.track) return;

        this.prevButton?.addEventListener('click', this.handlePrevClick);
        this.nextButton?.addEventListener('click', this.handleNextClick);
        this.track.addEventListener('scroll', this.handleUpdate, { passive: true });
        this.addEventListener('pointerenter', this.handleDeferredUpdate);
        this.addEventListener('focusin', this.handleDeferredUpdate);

        this.resizeObserver = new ResizeObserver(this.handleUpdate);
        this.resizeObserver.observe(this);
        this.resizeObserver.observe(this.track);

        requestAnimationFrame(() => this.updateState());
    }

    disconnectedCallback() {
        this.prevButton?.removeEventListener('click', this.handlePrevClick);
        this.nextButton?.removeEventListener('click', this.handleNextClick);
        this.track?.removeEventListener('scroll', this.handleUpdate);
        this.removeEventListener('pointerenter', this.handleDeferredUpdate);
        this.removeEventListener('focusin', this.handleDeferredUpdate);
        this.resizeObserver?.disconnect();
    }

    get scrollStep() {
        return Math.max(this.track.clientWidth * 0.6, 96);
    }

    scrollPrev() {
        this.track.scrollBy({ left: -this.scrollStep, behavior: 'smooth' });
    }

    scrollNext() {
        this.track.scrollBy({ left: this.scrollStep, behavior: 'smooth' });
    }

    updateState() {
        if (!this.track) return;

        const maxScrollLeft = this.track.scrollWidth - this.track.clientWidth;
        const isScrollable = maxScrollLeft > 4;
        const canScrollLeft = this.track.scrollLeft > 4;
        const canScrollRight = this.track.scrollLeft < maxScrollLeft - 4;

        this.classList.toggle('is-scrollable', isScrollable);
        this.classList.toggle('can-scroll-left', isScrollable && canScrollLeft);
        this.classList.toggle('can-scroll-right', isScrollable && canScrollRight);

        if (this.prevButton) {
            this.prevButton.hidden = !(isScrollable && canScrollLeft);
        }

        if (this.nextButton) {
            this.nextButton.hidden = !(isScrollable && canScrollRight);
        }
    }
}

customElements.define('swatch-scroller', SwatchScroller);

class ProductCard extends HTMLElement {
    constructor() {
        super();
        this.handleSwatchClick = this.onSwatchClick.bind(this);
        this.handleSwatchMouseOver = this.onSwatchMouseOver.bind(this);
        this.handleSwatchMouseOut = this.onSwatchMouseOut.bind(this);
        this.handleMediaNavigationClick = this.onMediaNavigationClick.bind(this);
        this.selectedSwatch = null;
        this.selectedProduct = null;
        this.previewSwatch = null;
        this.renderedProductId = '';
        this.mediaHtmlByProductId = new Map();
        this.isMediaPreviewing = false;
    }

    connectedCallback() {
        this.selectedSwatch = this.getSelectedSwatch();
        this.selectedProduct = this.getInitialSelectedProduct();
        this.renderedProductId = this.selectedProduct.id;
        this.dataset.currentProductId = this.renderedProductId;
        this.storeCurrentMedia(this.renderedProductId);
        this.addEventListener('click', this.handleSwatchClick);
        this.addEventListener('click', this.handleMediaNavigationClick, true);
        this.addEventListener('mouseover', this.handleSwatchMouseOver);
        this.addEventListener('mouseout', this.handleSwatchMouseOut);

        if (this.hasAttribute('data-product-card-media-on-visible') && this.dataset.productCardMediaDeferred === 'true') {
            // Warm swipe galleries shortly before deferred cards become visible.
            if ('IntersectionObserver' in window) {
                this.mediaObserver = new IntersectionObserver((entries) => {
                    if (!entries.some((entry) => entry.isIntersecting)) return;
                    this.loadDeferredCurrentMedia().then((loaded) => {
                        if (loaded) this.mediaObserver?.disconnect();
                    });
                }, { rootMargin: '200px' });
                this.mediaObserver.observe(this);
            } else {
                this.loadDeferredCurrentMedia();
            }
        }
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.handleSwatchClick);
        this.removeEventListener('click', this.handleMediaNavigationClick, true);
        this.removeEventListener('mouseover', this.handleSwatchMouseOver);
        this.removeEventListener('mouseout', this.handleSwatchMouseOut);
        this.mediaObserver?.disconnect();
    }

    onMediaNavigationClick(event) {
        const button = event.target.closest('.product-card-image-wrapper .image-prev-btn, .product-card-image-wrapper .image-next-btn');
        if (!button || !this.contains(button) || this.dataset.productCardMediaDeferred !== 'true') return;

        event.preventDefault();
        event.stopPropagation();

        const direction = button.classList.contains('image-prev-btn') ? 'prev' : 'next';
        this.loadDeferredCurrentMedia().then((loaded) => {
            if (!loaded) return;

            const slider = this.querySelector('.product-card-image-wrapper .swiper-slider');
            if (slider?.swiper) {
                direction === 'prev' ? slider.swiper.slidePrev() : slider.swiper.slideNext();
            }
        });
    }

    onSwatchClick(event) {
        const swatch = event.target.closest('[data-product-card-swatch]');
        if (!swatch || !this.contains(swatch)) return;
        if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        event.preventDefault();
        const forceMediaUpdate = this.isMediaPreviewing;
        this.previewSwatch = null;
        this.updateFromSwatch(swatch, { persist: true, forceMediaUpdate }).catch((error) => {
            console.error('Failed to update product card swatch', error);
        });
    }

    onSwatchMouseOver(event) {
        if (!this.shouldPreviewOnHover()) return;

        const swatch = event.target.closest('[data-product-card-swatch]');
        if (!swatch || !this.contains(swatch)) return;
        if (event.relatedTarget && swatch.contains(event.relatedTarget)) return;
        if (this.previewSwatch === swatch) return;

        this.previewSwatch = swatch;
        this.previewMedia(swatch);
        this.previewTitle(swatch);
    }

    onSwatchMouseOut(event) {
        if (!this.shouldPreviewOnHover()) return;

        const swatch = event.target.closest('[data-product-card-swatch]');
        if (!swatch || !this.contains(swatch)) return;
        if (event.relatedTarget && swatch.contains(event.relatedTarget)) return;
        if (this.previewSwatch !== swatch) return;

        this.previewSwatch = null;
        this.restoreSelectedMedia();
        this.restoreSelectedTitle();
    }

    async updateFromSwatch(swatch, { persist = true, updateActive = persist, replaceSwatches = persist, forceMediaUpdate = false } = {}) {
        const productId = swatch.dataset.productCardProductId;
        const productUrl = swatch.dataset.productCardUrl || swatch.getAttribute('href');
        const productTitle = swatch.dataset.productCardSwatchTitle || swatch.querySelector('img')?.alt || '';

        if (!productId || !productUrl) return;

        if (persist) {
            await this.ensureSwatchPayload(productId, productUrl);
        }

        if (persist) {
            this.selectedSwatch = swatch;
            this.selectedProduct = {
                id: productId,
                url: productUrl,
                title: productTitle
            };
            this.dataset.currentProductId = productId;
        }

        if (updateActive) {
            this.updateActiveSwatch(swatch);
        }

        const shouldUpdateMedia = forceMediaUpdate || this.renderedProductId !== productId;

        this.renderedProductId = productId;
        this.updateLinks(productUrl);
        this.updateTitle(productTitle, productUrl);
        this.updatePrice(swatch);
        this.updateAddToCartForm(swatch);
        this.updateBadge(swatch.dataset.productCardBadgeText || '');
        this.updateColours(swatch, productId, { replace: replaceSwatches });
        this.updateSizes(swatch, productId, { replace: replaceSwatches });

        if (shouldUpdateMedia) {
            this.updateMedia(productId);
        }
    }

    restoreSelectedSwatch() {
        const selectedSwatch = this.selectedSwatch || this.getSelectedSwatch();
        if (!selectedSwatch) return;

        this.updateFromSwatch(selectedSwatch, { persist: false, updateActive: false });
    }

    getSelectedSwatch() {
        return this.querySelector('[data-product-card-swatch].active-swatch');
    }

    getInitialSelectedProduct() {
        const titleLink = this.querySelector('[data-product-card-title]');

        return {
            id: this.dataset.productCardProductId || this.selectedSwatch?.dataset.productCardProductId || '',
            url: titleLink?.getAttribute('href')
                || this.selectedSwatch?.dataset.productCardUrl
                || this.selectedSwatch?.getAttribute('href')
                || '',
            title: titleLink?.textContent?.trim()
                || this.selectedSwatch?.dataset.productCardSwatchTitle
                || this.selectedSwatch?.querySelector('img')?.alt
                || ''
        };
    }

    shouldPreviewOnHover() {
        return window.matchMedia?.('(hover: hover) and (pointer: fine)').matches;
    }

    updateActiveSwatch(activeSwatch) {
        const swatchesWrapper = activeSwatch.closest('[data-swatch-scroll-track]') || this;
        swatchesWrapper.querySelectorAll('[data-product-card-swatch]').forEach((swatch) => {
            swatch.classList.toggle('active-swatch', swatch === activeSwatch);
        });
    }

    updateLinks(productUrl) {
        this.querySelectorAll('[data-product-card-link]').forEach((link) => {
            link.setAttribute('href', productUrl);
        });
    }

    updateTitle(productTitle, productUrl) {
        const titleLink = this.querySelector('[data-product-card-title]');
        if (!titleLink) return;

        titleLink.textContent = productTitle;
        titleLink.setAttribute('href', productUrl);
    }

    previewTitle(swatch) {
        const productUrl = swatch.dataset.productCardUrl || swatch.getAttribute('href');
        const productTitle = swatch.dataset.productCardSwatchTitle || swatch.querySelector('img')?.alt || '';
        if (!productTitle || !productUrl) return;

        this.updateTitle(productTitle, productUrl);
    }

    restoreSelectedTitle() {
        const productUrl = this.selectedProduct?.url || '';
        const productTitle = this.selectedProduct?.title || '';
        if (!productTitle || !productUrl) return;

        this.updateTitle(productTitle, productUrl);
    }

    updatePrice(swatch) {
        const priceWrapper = this.querySelector('[data-product-card-price-wrapper]');
        if (!priceWrapper) return;

        const price = swatch.dataset.productCardPrice || '';
        const comparePrice = swatch.dataset.productCardComparePrice || '';
        const priceCents = Number(swatch.dataset.productCardPriceCents || 0);
        const comparePriceCents = Number(swatch.dataset.productCardComparePriceCents || 0);
        const isDiscounted = comparePriceCents > priceCents;

        priceWrapper.innerHTML = '';

        const priceEl = document.createElement('span');
        priceEl.className = `${isDiscounted ? 'product-price text-watermelon-red ' : ''}text-very-dark-green`;
        priceEl.innerHTML = price;
        priceWrapper.appendChild(priceEl);

        if (!isDiscounted) return;

        const comparePriceEl = document.createElement('s');
        comparePriceEl.className = 'product-compare-price text-medium-green';
        comparePriceEl.innerHTML = comparePrice;
        priceWrapper.appendChild(document.createTextNode(' '));
        priceWrapper.appendChild(comparePriceEl);
    }

    updateAddToCartForm(swatch) {
        const variantId = swatch.dataset.productCardVariantId || '';
        const isAvailable = swatch.dataset.productCardAvailable !== 'false';

        if (variantId) {
            this.querySelectorAll('[data-product-card-variant-id-input]').forEach((input) => {
                input.value = variantId;
            });
        }

        this.querySelectorAll('[data-product-card-add-button]').forEach((button) => {
            const buttonText = button.querySelector('[data-product-card-add-button-text]');
            const text = isAvailable
                ? button.dataset.availableText || 'Add to cart'
                : button.dataset.soldOutText || 'Sold out';

            button.disabled = !isAvailable;

            if (buttonText) {
                buttonText.textContent = text;
            } else {
                button.textContent = text;
            }
        });
    }

    updateBadge(badgeText) {
        const badgeSlot = this.querySelector('[data-product-card-badge-slot]');
        if (!badgeSlot) return;

        badgeSlot.innerHTML = '';
        if (!badgeText) return;

        const badgeWrapper = document.createElement('div');
        badgeWrapper.className = 'product-badge-wrapper position-absolute top-10px start-10px';

        const badge = document.createElement('span');
        badge.className = 'product-badge text-white';
        badge.innerHTML = badgeText;

        badgeWrapper.appendChild(badge);
        badgeSlot.appendChild(badgeWrapper);
    }

    updateSizes(swatch, productId, { replace = true } = {}) {
        const sizeCount = Number(swatch.dataset.productCardSwatchSizeCount || 0);
        const isSizeSwatch = swatch.dataset.productCardSwatchKind === 'size';
        const sizeWrapper = this.querySelector('[data-product-card-size-wrapper]');
        const sizeCountEl = this.querySelector('[data-product-card-size-count]');

        if (sizeWrapper && sizeCountEl) {
            sizeWrapper.classList.toggle('d-none', sizeCount <= 0);
            sizeCountEl.textContent = sizeCount > 0
                ? `${sizeCount} ${sizeCount === 1 ? 'Size' : 'Sizes'} Available`
                : '';
        }

        const sizeTemplate = this.getProductTemplate('data-product-card-swatch-sizes', productId);
        const sizeSwatchesSlot = this.querySelector('[data-product-card-size-swatches-slot]');
        const sizeSwatchesHtml = sizeTemplate?.innerHTML?.trim() || '';

        if (replace && sizeSwatchesSlot && !isSizeSwatch && sizeTemplate) {
            sizeSwatchesSlot.innerHTML = sizeSwatchesHtml;
            this.updateSwatchScrollers(sizeSwatchesSlot);
        }
    }

    updateColours(swatch, productId, { replace = true } = {}) {
        if (!replace) return;

        const isSizeSwatch = swatch.dataset.productCardSwatchKind === 'size';
        if (!isSizeSwatch) return;

        const colourTemplate = this.getProductTemplate('data-product-card-swatch-colours', productId);
        const colourSwatches = this.querySelector('[data-product-card-colour-swatches]');
        const colourSwatchesHtml = colourTemplate?.innerHTML?.trim() || '';

        if (colourSwatches && colourSwatchesHtml) {
            colourSwatches.outerHTML = colourSwatchesHtml;
            this.updateSwatchScrollers(this);
        }
    }

    updateMedia(productId) {
        const slidesTemplate = this.getProductTemplate('data-product-card-swatch-slides', productId);
        const slidesHtml = slidesTemplate?.innerHTML?.trim() || '';
        if (!slidesHtml) return;

        this.mediaHtmlByProductId.set(productId, slidesHtml);
        this.replaceMediaSlides(slidesHtml);
        this.isMediaPreviewing = false;
        if (this.dataset.productCardMediaDeferred === 'true') {
            this.dataset.productCardMediaDeferred = 'false';
            this.mediaObserver?.disconnect();
        }
    }

    previewMedia(swatch) {
        const previewSlide = this.createPreviewSlide(swatch);
        if (!previewSlide) return;

        this.replaceMediaSlides(previewSlide.outerHTML, { reinitialize: false });
        this.isMediaPreviewing = true;
    }

    createPreviewSlide(swatch) {
        const imageSrc = swatch.dataset.productCardPreviewImageSrc;
        if (!imageSrc) {
            const productId = swatch.dataset.productCardProductId;
            const slidesTemplate = this.getProductTemplate('data-product-card-swatch-slides', productId);
            const firstSlide = slidesTemplate?.content?.querySelector('.swiper-slide');

            return firstSlide?.cloneNode(true) || null;
        }

        const slide = document.createElement('div');
        slide.className = 'swiper-slide d-flex w-100 h-auto';

        const image = document.createElement('img');
        image.src = imageSrc;
        image.className = 'product-card-image h-auto w-100 object-fit-cover';
        image.loading = 'lazy';
        image.alt = swatch.dataset.productCardPreviewImageAlt || '';

        const imageWidth = Number(swatch.dataset.productCardPreviewImageWidth || 0);
        const imageHeight = Number(swatch.dataset.productCardPreviewImageHeight || 0);

        if (imageWidth > 0) {
            image.width = imageWidth;
        }

        if (imageHeight > 0) {
            image.height = imageHeight;
        }

        slide.appendChild(image);
        return slide;
    }

    restoreSelectedMedia() {
        if (!this.isMediaPreviewing) return;

        const selectedProductId = this.selectedProduct?.id || this.renderedProductId || '';
        const selectedMediaHtml = this.mediaHtmlByProductId.get(selectedProductId);

        if (selectedMediaHtml) {
            this.replaceMediaSlides(selectedMediaHtml);
            this.isMediaPreviewing = false;
            return;
        }

        this.updateMedia(selectedProductId);
        this.isMediaPreviewing = false;
    }

    storeCurrentMedia(productId) {
        const sliderWrapper = this.querySelector('.product-card-image-wrapper .swiper-wrapper');
        if (!productId || !sliderWrapper || this.mediaHtmlByProductId.has(productId)) return;

        const slidesHtml = Array.from(sliderWrapper.children)
            .filter((slide) => !slide.classList.contains('swiper-slide-duplicate'))
            .map((slide) => slide.outerHTML)
            .join('');

        this.mediaHtmlByProductId.set(productId, slidesHtml || sliderWrapper.innerHTML);
    }

    replaceMediaSlides(slidesHtml, { reinitialize = true } = {}) {
        const slider = this.querySelector('.product-card-image-wrapper .swiper-slider');
        const sliderWrapper = this.querySelector('.product-card-image-wrapper .swiper-wrapper');
        if (!sliderWrapper || !slidesHtml) return;

        if (slider?.swiper && typeof slider.swiper.destroy === 'function') {
            slider.swiper.destroy(true, false);
        }

        sliderWrapper.innerHTML = slidesHtml;
        this.resetMediaWrapperStyles(sliderWrapper, { reinitialize });
        this.toggleMediaNavigation(reinitialize);

        if (!reinitialize || !slider || !window.Swiper) return;

        try {
            const sliderOptions = JSON.parse(slider.dataset.slider || '{}');
            slider.swiper = new Swiper(slider, sliderOptions);
        } catch (error) {
            console.error('Failed to reinitialize product card slider', error);
        }
    }

    resetMediaWrapperStyles(sliderWrapper, { reinitialize = true } = {}) {
        if (reinitialize) {
            sliderWrapper.style.transform = '';
            sliderWrapper.style.transitionDuration = '';
            sliderWrapper.style.transitionProperty = '';
            return;
        }

        sliderWrapper.style.transform = 'translate3d(0px, 0px, 0px)';
        sliderWrapper.style.transitionDuration = '0ms';
        sliderWrapper.style.transitionProperty = 'transform';
    }

    toggleMediaNavigation(isVisible) {
        this.querySelectorAll('.product-card-image-wrapper .image-prev-btn, .product-card-image-wrapper .image-next-btn').forEach((button) => {
            button.hidden = !isVisible;
        });
    }

    updateSwatchScrollers(container) {
        requestAnimationFrame(() => {
            container.querySelectorAll('swatch-scroller').forEach((scroller) => {
                scroller.updateState?.();
            });
        });
    }

    getProductTemplate(attributeName, productId) {
        return Array.from(this.querySelectorAll(`template[${attributeName}]`)).find((template) => {
            return template.getAttribute(attributeName) === productId;
        });
    }

    hasSwatchPayload(productId) {
        return this.getProductTemplate('data-product-card-swatch-slides', productId)
            || this.getProductTemplate('data-product-card-swatch-sizes', productId)
            || this.getProductTemplate('data-product-card-swatch-colours', productId);
    }

    async ensureSwatchPayload(productId, productUrl) {
        if (this.hasSwatchPayload(productId)) return true;

        const endpoint = this.getProductCardEndpoint(productUrl);
        if (!endpoint) return false;

        try {
            const html = await this.getProductCardHtml(endpoint);
            this.appendSwatchPayloadFromHtml(html);
            return true;
        } catch (error) {
            ProductCard.productCardPayloadCache.delete(endpoint);
            console.error('Failed to lazy-load product card swatch payload', error);
            return false;
        }
    }

    async loadDeferredCurrentMedia() {
        if (this.dataset.productCardMediaDeferred !== 'true') return true;
        if (this.mediaLoadPromise) return this.mediaLoadPromise;

        this.mediaLoadPromise = this.hydrateDeferredCurrentMedia();
        try {
            return await this.mediaLoadPromise;
        } finally {
            this.mediaLoadPromise = null;
        }
    }

    async hydrateDeferredCurrentMedia() {
        const productId = this.dataset.currentProductId || this.renderedProductId || this.dataset.productCardProductId;
        const productUrl = this.querySelector('[data-product-card-link]')?.getAttribute('href');
        if (!productId || !productUrl) return false;

        const endpoint = this.getProductCardEndpoint(productUrl, 'card-media');
        if (!endpoint) return false;

        try {
            const html = await this.getProductCardHtml(endpoint);
            if (!this.isConnected || productId !== this.dataset.currentProductId) return false;
            const loaded = this.appendCurrentMediaFromHtml(html, productId);
            if (loaded) {
                this.dataset.productCardMediaDeferred = 'false';
            }

            return loaded;
        } catch (error) {
            ProductCard.productCardPayloadCache.delete(endpoint);
            console.error('Failed to lazy-load product card media', error);
            return false;
        }
    }

    getProductCardHtml(endpoint) {
        if (!ProductCard.productCardPayloadCache.has(endpoint)) {
            ProductCard.productCardPayloadCache.set(
                endpoint,
                fetch(endpoint, { headers: { 'X-Requested-With': 'XMLHttpRequest' } }).then((response) => {
                    if (!response.ok) {
                        throw new Error(`Product card request failed with status ${response.status}`);
                    }

                    return response.text();
                })
            );
        }

        return ProductCard.productCardPayloadCache.get(endpoint);
    }

    getProductCardEndpoint(productUrl, view = 'card') {
        try {
            const url = new URL(productUrl, window.location.origin);
            url.searchParams.set('view', view);
            return url.toString();
        } catch (error) {
            console.error('Invalid product card URL', error);
            return '';
        }
    }

    appendSwatchPayloadFromHtml(html) {
        const productCard = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('product-card');

        if (!productCard) return;

        const payloadContainer = this.getLazySwatchPayloadContainer();
        const templateAttributes = [
            'data-product-card-swatch-slides',
            'data-product-card-swatch-sizes',
            'data-product-card-swatch-colours',
        ];

        productCard
            .querySelectorAll('[data-product-card-swatch-data] template, [data-product-card-size-swatch-data] template')
            .forEach((template) => {
                const attributeName = templateAttributes.find((attribute) => template.hasAttribute(attribute));
                if (!attributeName) return;

                const templateProductId = template.getAttribute(attributeName);
                if (!templateProductId || this.getProductTemplate(attributeName, templateProductId)) return;

                payloadContainer.appendChild(template.cloneNode(true));
            });
    }

    appendCurrentMediaFromHtml(html, productId) {
        const productCard = new DOMParser()
            .parseFromString(html, 'text/html')
            .querySelector('product-card');
        const sliderWrapper = productCard?.querySelector('.product-card-image-wrapper .swiper-wrapper');
        const slidesHtml = sliderWrapper?.innerHTML?.trim() || '';
        if (!slidesHtml) return false;

        this.mediaHtmlByProductId.set(productId, slidesHtml);
        if (!this.isMediaPreviewing) this.replaceMediaSlides(slidesHtml);
        return true;
    }

    getLazySwatchPayloadContainer() {
        let payloadContainer = this.querySelector('[data-product-card-lazy-swatch-payload]');
        if (payloadContainer) return payloadContainer;

        payloadContainer = document.createElement('div');
        payloadContainer.hidden = true;
        payloadContainer.setAttribute('data-product-card-lazy-swatch-payload', '');
        this.appendChild(payloadContainer);

        return payloadContainer;
    }
}

ProductCard.productCardPayloadCache = new Map();

if (!customElements.get('product-card')) {
    customElements.define('product-card', ProductCard);
}

const setCookieValue = (name, value, miliseconds = 1) => {
    const date = new Date();
    date.setTime(date.getTime() + (miliseconds));
    const expires = "expires=" + date.toUTCString();
    document.cookie = name + "=" + value + ";" + expires + ";path=/";
}

const getCookieValue = (name) => {
    const cookieArr = document.cookie.split(";");
    for(let i = 0; i < cookieArr.length; i++) {
        const cookie = cookieArr[i].trim();
        if (cookie.indexOf(name + "=") == 0) {
            return cookie.substring((name + "=").length, cookie.length);
        }
    }
    return null;
}

class CustomCalculateShipping extends HTMLElement {
    static postcodeIndexPromise = null;
    static postcodeLocationList = null;
    static ipLocationPromise = null;

    constructor() {
        super();
        this.graphqlStorefrontApiToken = 'f9b18e208880eb142730252ba6470ba8';
        this.graphqlApiUrl = 'https://ats-sydney.myshopify.com/api/2024-10/graphql.json';
        this.zipcodeCookieKey = 'postcode_search';
        this.locationCookieKey = 'postcode_location';
        this.ipLocationAttemptKey = 'postcode_ip_location_attempted_at_v4';
        this.ipLocationAttemptTtl = 24 * 3600 * 1000;
        this.shippingCalucationType = this.getAttribute('data-calculation-type');
        this.isProductCalculation = this.shippingCalucationType === 'product';
        this.shippingCalculateButton = this.querySelector('[data-shipping-calculate]');
        this.shippingOptionsEl = this.querySelector('[data-shipping-options]');
        this.shippingZipcodeEl = this.querySelector('[data-shipping-zipcode]');
        this.shippingErrorEl = this.querySelector('[data-shipping-error]');
        this.shippingLoaderEl = this.querySelector('[data-shipping-loader]');
        this.shippingLocationToggle = this.querySelector('[data-shipping-location-toggle]');
        this.shippingLocationLabel = this.querySelector('[data-shipping-location-label]');
        this.shippingPostcodePanel = this.querySelector('[data-shipping-postcode-panel]');
        this.shippingPostcodeForm = this.querySelector('[data-shipping-postcode-form]');
        this.shippingPostcodeSuggestions = this.querySelector('[data-shipping-postcode-suggestions]');
        this.postcodesUrl = this.dataset.postcodesUrl || '';
        this.countryCode = (this.dataset.countryCode || 'AU').toUpperCase();
        this.isAustralia = this.countryCode === 'AU';
        this.hasLocationUi = Boolean(this.shippingLocationToggle && this.shippingPostcodePanel);
        this.postcodeLength = parseInt(this.dataset.postcodeLength, 10) || (this.countryCode === 'US' ? 5 : 4);
        this.defaultErrorMessage = this.shippingErrorEl?.textContent?.trim() || 'Something went wrong. Please check shipping rates in cart or at checkout. If it is still not working, please contact us.';
        this.selectedLocation = null;
        this.lookupRequestId = 0;

        this.handleHeaderPostcodeUpdate = this.onHeaderPostcodeUpdate.bind(this);
        this.handleLocationToggle = this.onLocationToggle.bind(this);
        this.handleProductPostcodeSubmit = this.onProductPostcodeSubmit.bind(this);
        this.handleProductPostcodeInput = this.onProductPostcodeInput.bind(this);
        this.handleProductSuggestionClick = this.onProductSuggestionClick.bind(this);
        this.handleDocumentClick = this.onDocumentClick.bind(this);
        this.handleTileQuantityUpdate = this.onTileQuantityUpdate.bind(this);
        this.tileShippingUpdateTimer = null;

        this.init();
        this.bindEvents();
    }

    disconnectedCallback() {
        window.removeEventListener('postcode:updated', this.handleHeaderPostcodeUpdate);
        this.shippingLocationToggle?.removeEventListener('click', this.handleLocationToggle);
        this.shippingPostcodeForm?.removeEventListener('submit', this.handleProductPostcodeSubmit);
        this.shippingZipcodeEl?.removeEventListener('input', this.handleProductPostcodeInput);
        this.shippingZipcodeEl?.removeEventListener('focus', this.handleProductPostcodeInput);
        this.shippingPostcodeSuggestions?.removeEventListener('click', this.handleProductSuggestionClick);
        document.removeEventListener('click', this.handleDocumentClick);
        window.removeEventListener('tile:quantity-change', this.handleTileQuantityUpdate);
        window.clearTimeout(this.tileShippingUpdateTimer);
    }

    init() {
        if (this.hasLocationUi) {
            this.initProductShipping();
            return;
        }

        let zipcodeCookie = getCookieValue(this.zipcodeCookieKey);
        if(zipcodeCookie) {
            if (this.shippingZipcodeEl) {
                this.shippingZipcodeEl.value = zipcodeCookie;
            }

            this.handleCalculateShipping();
            return;
        }

        this.scheduleIpLocationLookup();
    }

    initProductShipping() {
        this.hydrateFromDefaultLocation();
        this.syncProductLocationFromCookie();

        const postcode = this.getActivePostcode();
        if (postcode) {
            if (this.shippingZipcodeEl) {
                this.shippingZipcodeEl.value = postcode;
            }

            this.closeProductPostcodePanel();
            this.handleCalculateShipping();
            return;
        }

        this.updateProductUi(null);
        this.openProductPostcodePanel({ focus: false });
        this.scheduleIpLocationLookup();
    }

    bindEvents() {
        this.shippingCalculateButton?.addEventListener("click", () => this.handleCalculateShipping());
        window.addEventListener('postcode:updated', this.handleHeaderPostcodeUpdate);
        window.addEventListener('tile:quantity-change', this.handleTileQuantityUpdate);

        if (!this.hasLocationUi) return;

        this.shippingLocationToggle?.addEventListener('click', this.handleLocationToggle);
        this.shippingPostcodeForm?.addEventListener('submit', this.handleProductPostcodeSubmit);
        this.shippingZipcodeEl?.addEventListener('input', this.handleProductPostcodeInput);
        this.shippingZipcodeEl?.addEventListener('focus', this.handleProductPostcodeInput);
        this.shippingPostcodeSuggestions?.addEventListener('click', this.handleProductSuggestionClick);
        document.addEventListener('click', this.handleDocumentClick);
    }

    scheduleIpLocationLookup() {
        if (!this.shouldUseIpLocationLookup()) return;

        const runLookup = () => {
            this.hydrateFromIpLocation().catch((error) => {
                console.warn('IP postcode lookup failed', error);
            });
        };

        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(runLookup, { timeout: 3000 });
            return;
        }

        window.setTimeout(runLookup, 700);
    }

    shouldUseIpLocationLookup() {
        if (!this.shippingZipcodeEl || !this.isAustralia) return false;
        if (this.getActivePostcode()) return false;
        if (!CustomCalculateShipping.ipLocationPromise && this.hasRecentIpLocationAttempt()) return false;

        return true;
    }

    async hydrateFromIpLocation() {
        if (!this.shouldUseIpLocationLookup()) return;

        const location = await this.resolveIpLocation();
        if (!location || this.getActivePostcode()) return;

        this.persistProductLocation(location);
    }

    async resolveIpLocation() {
        if (!CustomCalculateShipping.ipLocationPromise) {
            if (this.hasRecentIpLocationAttempt()) return null;
            this.markIpLocationAttempt();
        }

        const ipLocation = await CustomCalculateShipping.fetchIpApiLocation();
        return this.normalizeIpLocation(ipLocation);
    }

    async normalizeIpLocation(ipLocation) {
        if (!ipLocation || ipLocation.error) return null;

        const locationData = Array.isArray(ipLocation) ? ipLocation[0] : ipLocation;
        if (!locationData) return null;

        const ipifyLocation = locationData.location || {};
        const countryCode = (ipifyLocation.country || locationData.countryCode || locationData.country_code || locationData.country || '').toUpperCase();
        if (countryCode && countryCode !== 'AU') return null;

        const postcode = this.normalizeLookupPostcode(locationData.ipapiPostal || ipifyLocation.postalCode || locationData.zipCode || locationData.postal_code || locationData.postal || locationData.postcode || locationData.zip || '');
        if (!this.isValidRawPostcode(postcode)) return null;

        const locations = await this.findLocationsByPostcode(postcode);

        if (Array.isArray(locations) && locations.length) {
            return locations[0];
        }

        return null;
    }

    static async fetchIpApiLocation() {
        if (!CustomCalculateShipping.ipLocationPromise) {
            CustomCalculateShipping.ipLocationPromise = fetch('https://ipapi.co/postal/', {
                credentials: 'omit',
                headers: {
                    Accept: 'text/plain'
                }
            })
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`ipapi postal lookup failed: ${response.status}`);
                    }

                    return response.text();
                })
                .then((postal) => {
                    return {
                        ipapiPostal: postal
                    };
                })
                .catch((error) => {
                    console.warn('ipapi postal lookup failed', error);
                    return null;
                });
        }

        return CustomCalculateShipping.ipLocationPromise;
    }

    onHeaderPostcodeUpdate(event) {
        const detail = event?.detail || {};
        const postcode = detail.postcode;
        if (!postcode || !this.shippingZipcodeEl) return;

        if (this.hasLocationUi) {
            const location = detail.suburb && detail.province
                ? {
                    suburb: detail.suburb,
                    state: detail.province,
                    postcode: detail.postcode
                }
                : this.getStoredLocation();

            this.selectedLocation = location;
            this.shippingZipcodeEl.value = this.normalizeLookupPostcode(postcode);
            this.updateProductUi(location || postcode);
            this.hideProductPostcodeSuggestions();
            this.closeProductPostcodePanel();
        } else {
            this.shippingZipcodeEl.value = postcode;
        }

        this.handleCalculateShipping();
    }

    onLocationToggle(event) {
        event.preventDefault();

        if (this.shippingPostcodePanel?.hidden) {
            this.openProductPostcodePanel({
                focus: true,
                immediateFocus: this.shouldUseImmediateFocus()
            });
            return;
        }

        if (this.getActivePostcode()) {
            this.closeProductPostcodePanel();
        }
    }

    onDocumentClick(event) {
        if (!this.hasLocationUi || !this.getActivePostcode()) return;
        if (this.shippingPostcodePanel?.hidden || this.contains(event.target)) return;

        this.closeProductPostcodePanel();
    }

    async onProductPostcodeSubmit(event) {
        event.preventDefault();

        const searchValue = this.normalizeSearchValue(this.shippingZipcodeEl?.value || '').trim();
        if (!searchValue) {
            this.showShippingError(this.defaultErrorMessage);
            return;
        }

        if (!this.isAustralia) {
            const postcode = this.normalizePostcode(searchValue);
            if (!this.isValidRawPostcode(postcode)) {
                this.showShippingError(this.defaultErrorMessage);
                return;
            }

            this.persistProductRawPostcode(postcode);
            return;
        }

        const locations = await this.findLocationsBySearch(searchValue);
        if (locations === null) {
            const postcode = this.normalizePostcode(searchValue);
            const province = this.getProvinceCode(postcode);
            if (this.isValidRawPostcode(postcode) && province) {
                this.persistProductRawPostcode(postcode, province);
                return;
            }

            this.showShippingError(this.defaultErrorMessage);
            return;
        }

        if (locations.length === 1) {
            this.persistProductLocation(locations[0]);
            return;
        }

        if (locations.length > 1) {
            this.renderProductPostcodeSuggestions(locations);
            this.showShippingError('Please select your suburb from the list.');
            return;
        }

        this.showShippingError(this.defaultErrorMessage);
    }

    async onProductPostcodeInput() {
        if (!this.shippingZipcodeEl) return;

        const searchValue = this.normalizeSearchValue(this.shippingZipcodeEl.value);
        if (searchValue !== this.shippingZipcodeEl.value) {
            this.shippingZipcodeEl.value = searchValue;
        }

        this.hideShippingError();

        if (!this.isAustralia) {
            this.hideProductPostcodeSuggestions();
            return;
        }

        if (!this.selectedLocation || !this.isLocationMatchForSearch(this.selectedLocation, searchValue)) {
            this.selectedLocation = null;
        }

        if (!this.shouldSearchLocations(searchValue)) {
            this.hideProductPostcodeSuggestions();
            return;
        }

        const requestId = this.lookupRequestId + 1;
        this.lookupRequestId = requestId;

        const locations = await this.findLocationsBySearch(searchValue);
        if (requestId !== this.lookupRequestId) return;

        if (locations) {
            this.renderProductPostcodeSuggestions(locations);
        } else {
            this.hideProductPostcodeSuggestions();
        }
    }

    onProductSuggestionClick(event) {
        const trigger = event.target.closest('[data-shipping-postcode-suggestion]');
        if (!trigger) return;

        event.preventDefault();

        const location = {
            suburb: trigger.dataset.suburb || '',
            state: trigger.dataset.state || '',
            postcode: trigger.dataset.postcode || ''
        };

        if (!location.suburb || !location.state || !location.postcode) return;
        this.persistProductLocation(location);
    }

    async handleCalculateShipping() {
        this.hideShippingError();

        const zipcode = this.getCalculationPostcode();
        if(zipcode) {
            this.setLoadingState(true);

            if (this.isProductCalculation && this.selectedLocation) {
                this.setStoredLocation(this.selectedLocation);
            }

            setCookieValue(this.zipcodeCookieKey, zipcode, (31 * 24 * 3600 * 1000));

            const hasRates = this.shippingCalucationType === 'product'
                ? await this.calculateProductShipping(zipcode)
                : await this.calculateCartShipping(zipcode);

            this.setLoadingState(false);

            if (!hasRates) {
                this.showShippingError(this.defaultErrorMessage);
            }
        }
        else {
            this.showShippingError(this.defaultErrorMessage);
        }
    }

    getCalculationPostcode() {
        if (this.hasLocationUi) {
            return this.getActivePostcode();
        }

        return this.normalizeLookupPostcode(this.shippingZipcodeEl?.value || '');
    }

    onTileQuantityUpdate(event) {
        if (!this.isProductCalculation) return;

        const productRoot = this.closest('[data-product-main]') || document;
        if (event?.detail?.productRoot && event.detail.productRoot !== productRoot) return;
        if (!this.getCalculationPostcode()) return;

        window.clearTimeout(this.tileShippingUpdateTimer);
        this.tileShippingUpdateTimer = window.setTimeout(() => {
            this.handleCalculateShipping();
        }, 350);
    }

    getProductShippingQuantity() {
        const productRoot = this.closest('[data-product-main]') || document;
        const tileQuantity = this.getTileProductShippingQuantity(productRoot);
        if (tileQuantity) return tileQuantity;

        const quantityInput = productRoot.querySelector('[data-tile-quantity-input], form[action*="/cart/add"] [name="quantity"], input[name="quantity"]');
        const quantity = Math.ceil(Number(quantityInput?.value || 1));

        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    getTileProductShippingQuantity(productRoot) {
        const calculator = productRoot.querySelector('[data-tile-calculator]');
        if (!calculator) return 0;

        const m2PerBox = Math.max(Number(calculator.dataset.m2PerBox || 0), 0.01);
        if (!Number.isFinite(m2PerBox) || m2PerBox <= 0) return 0;

        const areaInput = productRoot.querySelector('[data-tile-area-input]');
        const wastageInput = productRoot.querySelector('[data-tile-wastage-toggle]');
        const area = Math.max(parseFloat(areaInput?.value || '1'), 0.01);
        const effectiveArea = area * (wastageInput?.checked ? 1.15 : 1);
        const quantity = Math.ceil(effectiveArea / m2PerBox);

        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    setLoadingState(isLoading) {
        this.shippingLoaderEl?.classList?.toggle('d-none', !isLoading);
        this.shippingCalculateButton?.classList?.toggle('custom-loading', isLoading);

        if (!this.hasLocationUi || !this.shippingOptionsEl) return;

        this.classList.toggle('is-shipping-loading', isLoading);

        if (isLoading) {
            this.shippingOptionsEl.innerHTML = '<div class="shipping-rate-info shipping-rate-info--loading">Calculating shipping options...</div>';
            this.shippingOptionsEl.classList.remove('d-none');
        }
    }

    hydrateFromDefaultLocation() {
        if (!this.isAustralia) return;
        if (this.getCookieValue(this.zipcodeCookieKey) || this.getStoredLocation()) return;

        const defaultLocation = this.getDefaultLocation();
        if (!defaultLocation) return;

        this.selectedLocation = defaultLocation;
        this.setCookieValue(this.zipcodeCookieKey, defaultLocation.postcode, 31 * 24 * 3600 * 1000);
        this.setStoredLocation(defaultLocation);
    }

    syncProductLocationFromCookie() {
        const storedLocation = this.getStoredLocation();
        const storedPostcode = this.getCookieValue(this.zipcodeCookieKey) || '';

        this.selectedLocation = storedLocation;
        this.updateProductUi(storedLocation || storedPostcode);
    }

    getActivePostcode() {
        return this.normalizeLookupPostcode(
            this.selectedLocation?.postcode || this.getCookieValue(this.zipcodeCookieKey) || this.shippingZipcodeEl?.value || ''
        );
    }

    updateProductUi(locationOrPostcode) {
        const location = typeof locationOrPostcode === 'object' && locationOrPostcode !== null
            ? {
                suburb: locationOrPostcode.suburb || '',
                state: (locationOrPostcode.state || '').toUpperCase(),
                postcode: this.normalizeLookupPostcode(locationOrPostcode.postcode || '')
            }
            : null;
        const postcode = location?.postcode || this.normalizeLookupPostcode(locationOrPostcode || '');

        if (this.shippingZipcodeEl && document.activeElement !== this.shippingZipcodeEl) {
            this.shippingZipcodeEl.value = postcode;
        }

        if (this.shippingLocationLabel) {
            this.shippingLocationLabel.textContent = postcode
                ? `Deliver to ${this.getDisplayLocation(location, postcode)}`
                : 'Enter postcode or suburb';
        }

        this.classList.toggle('has-postcode', Boolean(postcode));
    }

    getDisplayLocation(location = null, postcode = '') {
        if (location?.suburb && location?.state && location?.postcode) {
            return `${location.suburb}, ${location.state} ${location.postcode}`;
        }

        const province = this.getProvinceCode(postcode);
        return province ? `${province}, ${postcode}` : postcode;
    }

    openProductPostcodePanel({ focus = false, immediateFocus = false } = {}) {
        if (!this.shippingPostcodePanel) return;

        this.shippingPostcodePanel.hidden = false;
        this.classList.add('is-postcode-panel-open');
        this.shippingLocationToggle?.setAttribute('aria-expanded', 'true');
        this.hideShippingError();
        this.hideProductPostcodeSuggestions();

        if (this.shippingZipcodeEl) {
            this.shippingZipcodeEl.value = '';
        }

        if (this.shippingOptionsEl) {
            this.shippingOptionsEl.classList.add('d-none');
        }

        if (!focus || !this.shippingZipcodeEl) return;

        const focusInput = () => {
            try {
                this.shippingZipcodeEl.focus({ preventScroll: true });
            } catch (error) {
                this.shippingZipcodeEl.focus();
            }
        };

        if (immediateFocus) {
            focusInput();
        } else {
            requestAnimationFrame(focusInput);
        }
    }

    shouldUseImmediateFocus() {
        const platform = navigator.platform || '';
        const userAgent = navigator.userAgent || '';

        return /iPad|iPhone|iPod/.test(userAgent) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    }

    closeProductPostcodePanel() {
        if (!this.shippingPostcodePanel) return;

        this.shippingPostcodePanel.hidden = true;
        this.classList.remove('is-postcode-panel-open');
        this.shippingLocationToggle?.setAttribute('aria-expanded', 'false');
        this.hideProductPostcodeSuggestions();
        this.hideShippingError();

        if (this.shippingOptionsEl?.innerHTML?.trim()) {
            this.shippingOptionsEl.classList.remove('d-none');
        }
    }

    persistProductRawPostcode(postcode, province = null) {
        const normalizedPostcode = this.normalizeLookupPostcode(postcode);

        this.selectedLocation = null;
        this.clearStoredLocation();
        this.setCookieValue(this.zipcodeCookieKey, normalizedPostcode, 31 * 24 * 3600 * 1000);
        this.updateProductUi(normalizedPostcode);
        this.hideProductPostcodeSuggestions();
        this.closeProductPostcodePanel();

        const detail = {
            postcode: normalizedPostcode,
            countryCode: this.countryCode || 'AU'
        };

        if (province) {
            detail.province = province;
        }

        this.dispatchPostcodeUpdate(detail);
    }

    persistProductLocation(location) {
        const normalizedLocation = {
            suburb: location.suburb || '',
            state: (location.state || '').toUpperCase(),
            postcode: this.normalizeLookupPostcode(location.postcode || '')
        };

        this.selectedLocation = normalizedLocation;
        this.setCookieValue(this.zipcodeCookieKey, normalizedLocation.postcode, 31 * 24 * 3600 * 1000);
        this.setStoredLocation(normalizedLocation);
        this.updateProductUi(normalizedLocation);
        this.hideProductPostcodeSuggestions();
        this.closeProductPostcodePanel();
        this.dispatchPostcodeUpdate({
            postcode: normalizedLocation.postcode,
            province: normalizedLocation.state,
            suburb: normalizedLocation.suburb,
            label: `Deliver to ${this.getDisplayLocation(normalizedLocation)}`,
            countryCode: 'AU'
        });
    }

    dispatchPostcodeUpdate(detail) {
        window.dispatchEvent(
            new CustomEvent('postcode:updated', {
                detail
            })
        );
    }

    renderProductPostcodeSuggestions(locations) {
        if (!this.shippingPostcodeSuggestions) return;

        if (!locations?.length) {
            this.hideProductPostcodeSuggestions();
            return;
        }

        const activeKey = this.selectedLocation ? this.getLocationKey(this.selectedLocation) : '';

        this.shippingPostcodeSuggestions.innerHTML = locations.map((location) => {
            const normalizedLocation = {
                suburb: location.suburb || '',
                state: (location.state || '').toUpperCase(),
                postcode: this.normalizeLookupPostcode(location.postcode || '')
            };
            const locationKey = this.getLocationKey(normalizedLocation);
            const activeClass = locationKey === activeKey ? ' is-active' : '';
            const locationLabel = this.escapeHtml(`${normalizedLocation.postcode} ${normalizedLocation.suburb}, ${normalizedLocation.state}`);

            return `
                <button
                    class="shipping-postcode-suggestion${activeClass}"
                    type="button"
                    data-shipping-postcode-suggestion
                    data-suburb="${this.escapeAttribute(normalizedLocation.suburb)}"
                    data-state="${this.escapeAttribute(normalizedLocation.state)}"
                    data-postcode="${this.escapeAttribute(normalizedLocation.postcode)}"
                >
                    ${locationLabel}
                </button>
            `;
        }).join('');

        this.classList.add('has-postcode-suggestions');
        this.shippingPostcodeSuggestions.hidden = false;
    }

    hideProductPostcodeSuggestions() {
        if (!this.shippingPostcodeSuggestions) return;

        this.classList.remove('has-postcode-suggestions');
        this.shippingPostcodeSuggestions.hidden = true;
        this.shippingPostcodeSuggestions.innerHTML = '';
    }

    showShippingError(message) {
        if (!this.shippingErrorEl) return;

        this.shippingErrorEl.textContent = message || this.defaultErrorMessage;
        this.shippingErrorEl.classList.remove('d-none');

        if (this.hasLocationUi && message !== 'Please select your suburb from the list.') {
            this.shippingOptionsEl?.classList?.add('d-none');
        }
    }

    hideShippingError() {
        if (!this.shippingErrorEl) return;

        this.shippingErrorEl.textContent = this.defaultErrorMessage;
        this.shippingErrorEl.classList.add('d-none');
    }

    getProvinceCode(zipcode) {
        zipcode = parseInt(zipcode, 10);
        let province = null;
        if ((zipcode>= 1000 && zipcode<= 1999) || (zipcode>= 2000 && zipcode<= 2599) || (zipcode>= 2619 && zipcode<= 2899) || (zipcode>= 2921 && zipcode<= 2999)) {
            province = "NSW";
        }
        else if ((zipcode>= 200 && zipcode<= 299) || (zipcode>= 2600 && zipcode<= 2618) || (zipcode>= 2900 && zipcode<= 2920)) {
            province = "ACT";
        }
        else if ((zipcode>= 3000 && zipcode<= 3999) || (zipcode>= 8000 && zipcode<= 8999)) {
            province = "VIC";
        }
        else if ((zipcode>= 4000 && zipcode<= 4999) || (zipcode>= 9000 && zipcode<= 9999)) {
            province = "QLD";
        }
        else if ((zipcode>= 5000 && zipcode<= 5799) || (zipcode>= 5800 && zipcode<= 5999)) {
            province = "SA";
        }
        else if ((zipcode>= 6000 && zipcode<= 6797) || (zipcode>= 6800 && zipcode<= 6999)) {
            province = "WA";
        }
        else if ((zipcode>= 7000 && zipcode<= 7799) || (zipcode>= 7800 && zipcode<= 7999)) {
            province = "TAS";
        }
        else if ((zipcode>= 800 && zipcode<= 899) || (zipcode>= 900 && zipcode<= 999)) {
            province = "NT";
        }

        return province;
    }

    normalizeStateCode(value) {
        const normalizedValue = (value || '').trim().toUpperCase();
        const compactValue = normalizedValue.replace(/[^A-Z]/g, '');
        const stateMap = {
            ACT: 'ACT',
            AUSTRALIANCAPITALTERRITORY: 'ACT',
            NSW: 'NSW',
            NEWSOUTHWALES: 'NSW',
            NT: 'NT',
            NORTHERNTERRITORY: 'NT',
            QLD: 'QLD',
            QUEENSLAND: 'QLD',
            SA: 'SA',
            SOUTHAUSTRALIA: 'SA',
            TAS: 'TAS',
            TASMANIA: 'TAS',
            VIC: 'VIC',
            VICTORIA: 'VIC',
            WA: 'WA',
            WESTERNAUSTRALIA: 'WA'
        };

        return stateMap[compactValue] || '';
    }

    hasRecentIpLocationAttempt() {
        const attemptedAt = parseInt(this.getLocalStorageValue(this.ipLocationAttemptKey) || '0', 10);
        if (!attemptedAt) return false;

        return Date.now() - attemptedAt < this.ipLocationAttemptTtl;
    }

    markIpLocationAttempt() {
        this.setLocalStorageValue(this.ipLocationAttemptKey, Date.now().toString());
    }

    async calculateProductShipping(zipcode) {
        let queryParams = new URLSearchParams(window.location.search);
        let variantId = queryParams.get('variant') || this.closest('[data-product-container]')?.dataset.variantId || document.querySelector('[data-product-container]')?.dataset.variantId;

        if (!variantId) {
            return false;
        }

        zipcode = this.normalizeLookupPostcode(zipcode || '0');
        const zipcodeValue = parseInt(zipcode || '0', 10);
        let province = this.selectedLocation?.state || this.getProvinceCode(zipcode);
        let country = 'Australia';

        if (!zipcodeValue || !province) return false;

        let cartQuery = `mutation cartCreate($input: CartInput!) {
            cartCreate(input: $input) {
              cart {
                id
              }
              userErrors {
                message
              }
            }
        }`;

        const cartLine = {
            "merchandiseId": `gid://shopify/ProductVariant/${variantId}`,
            "quantity": this.getProductShippingQuantity()
        };
        const customProductAttributes = this.getCustomProductCartLineAttributes();

        if (customProductAttributes.length) {
            cartLine.attributes = customProductAttributes;
        }

        let cartVariables = {
            "input": {
                "lines": [
                    cartLine
                ]
            }
        }

        const headers = {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': this.graphqlStorefrontApiToken,
        };

        try {
            let response = await fetch(this.graphqlApiUrl, {
                method: 'POST',
                headers: headers,
                body: JSON.stringify({query: cartQuery, variables: cartVariables}),
            });
            response = await response.json();

            let cartId = response?.data?.cartCreate?.cart?.id;
            if (!cartId) return false;

            const shippingRatesQuery = `
                mutation cartBuyerIdentityUpdate($buyerIdentity: CartBuyerIdentityInput!, $cartId: ID!) {
                    cartBuyerIdentityUpdate(buyerIdentity: $buyerIdentity, cartId: $cartId) {
                      cart {
                        id
                        ... DeliveryGroups @defer
                      }
                      userErrors {
                        message
                      }
                    }
                  }
                  fragment DeliveryGroups on Cart {
                    deliveryGroups(first: 10, withCarrierRates: true) {
                      edges {
                        node {
                          deliveryOptions {
                            title
                            description
                            handle
                            estimatedCost {
                              amount
                              currencyCode
                            }
                          }
                          selectedDeliveryOption {
                            title
                            description
                            handle
                            estimatedCost {
                              amount
                              currencyCode
                            }
                          }
                        }
                      }
                    }
                  }
              `;

            const shippingRatesVariables = {
                cartId: cartId,
                buyerIdentity: {
                    countryCode: 'AU',
                    deliveryAddressPreferences: [
                        {
                            deliveryAddress: {
                                country: country,
                                province: province,
                                zip: zipcode
                            },
                            "deliveryAddressValidationStrategy": "COUNTRY_CODE_ONLY",
                            "oneTimeUse": true
                        }
                    ],
                }
            };

            try {
                let shippingResponse = await fetch(this.graphqlApiUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-Shopify-Storefront-Access-Token': this.graphqlStorefrontApiToken,
                    },
                    body: JSON.stringify({ query: shippingRatesQuery, variables: shippingRatesVariables }),
                });
                shippingResponse = await shippingResponse.text();
                const shippingRates = this.extractProductShippingRates(shippingResponse);

                if (!shippingRates.length) {
                    this.shippingOptionsEl?.classList?.add('d-none');
                    return false;
                }

                if (this.shippingOptionsEl) {
                    this.shippingOptionsEl.innerHTML = this.renderShippingRates(shippingRates, province, zipcode);
                    this.shippingOptionsEl.classList.remove('d-none');
                }

                return true;
            }
            catch(e) {
                console.error('Shipping rate api failed => ', e);
                return false;
            }
        }
        catch (e) {
            console.error('Calculate shipping cart api failed => ', e);
            return false;
        }
    }

    getCustomProductCartLineAttributes() {
        const productRoot = this.closest('[data-product-main]') || document;
        const customProductInput = productRoot.querySelector('[name="properties[_custom_product]"]');

        if (!customProductInput) return [];

        const width = this.getCustomSizePropertyValue(productRoot, 'width_raw', '[data-custom-size-width]');
        const height = this.getCustomSizePropertyValue(productRoot, 'height_raw', '[data-custom-size-height]');
        const priceCents = this.getCustomSizePropertyValue(productRoot, 'price_cents')
            || this.calculateCustomProductPriceCents(productRoot, width, height);
        const customProductValue = customProductInput.value || 'true';

        return [
            { key: '_custom_width_mm', value: width },
            { key: '_custom_height_mm', value: height },
            { key: '_custom_calculated_price_cents', value: priceCents },
            { key: '_custom_product', value: customProductValue }
        ];
    }

    getCustomSizePropertyValue(productRoot, propertyName, fallbackSelector = '') {
        const propertyInput = productRoot.querySelector(`[data-custom-size-property="${propertyName}"]`);
        const propertyValue = String(propertyInput?.value || '').trim();

        if (propertyValue || !fallbackSelector) return propertyValue;

        return String(productRoot.querySelector(fallbackSelector)?.value || '').trim();
    }

    calculateCustomProductPriceCents(productRoot, width, height) {
        const calculator = productRoot.querySelector('custom-product-size-calculator');
        if (!calculator) return '';

        const widthValue = Number(width);
        const heightValue = Number(height);
        const basePrice = Number(calculator.dataset.basePrice || 0);
        const areaMultiplier = Number(calculator.dataset.areaMultiplier || 0);
        const minPriceCents = Number(calculator.dataset.minPriceCents || 0);

        if (!Number.isFinite(widthValue) || !Number.isFinite(heightValue)) return '';
        if (!Number.isFinite(basePrice) || !Number.isFinite(areaMultiplier)) return '';

        const areaSquareMetres = (widthValue * heightValue) / 1000000;
        const calculatedPrice = basePrice + (areaSquareMetres * areaMultiplier);
        const minPrice = Number.isFinite(minPriceCents) ? minPriceCents / 100 : 0;
        const finalPrice = this.roundUpToEndingNine(Math.max(calculatedPrice, minPrice));

        return String(Math.round(finalPrice * 100));
    }

    roundUpToEndingNine(value) {
        const roundedInteger = Math.ceil(Number(value) || 0);
        const remainder = roundedInteger % 10;
        if (remainder === 9) return roundedInteger;

        return roundedInteger + ((9 - remainder + 10) % 10);
    }

    extractProductShippingRates(responseText) {
        const shippingRates = [];
        const parseDeliveryGroups = (deliveryGroups) => {
            const groups = deliveryGroups?.edges || [];
            groups.forEach((edge) => {
                const deliveryOptions = edge?.node?.deliveryOptions || [];
                deliveryOptions.forEach((rate) => shippingRates.push(rate));
            });
        };

        const parseJsonData = (jsonData) => {
            if (jsonData?.data?.cartBuyerIdentityUpdate?.cart?.deliveryGroups) {
                parseDeliveryGroups(jsonData.data.cartBuyerIdentityUpdate.cart.deliveryGroups);
            }

            if (!jsonData?.incremental?.length) return;

            jsonData.incremental.forEach((increment) => {
                if (increment.data?.deliveryGroups) {
                    parseDeliveryGroups(increment.data.deliveryGroups);
                }
            });
        };

        try {
            parseJsonData(JSON.parse(responseText));
            return shippingRates;
        } catch (error) {}

        const parts = responseText.split('--graphql');
        for (let part of parts) {
            const jsonStartIndex = part.indexOf('{');
            if (jsonStartIndex === -1) continue;

            try {
                parseJsonData(JSON.parse(part.slice(jsonStartIndex)));
            } catch (error) {}
        }

        return shippingRates;
    }

    renderShippingRates(shippingRates, province, zipcode) {
        let shippingData = `
            <ul class="shipping-rates d-flex flex-column">`;

        shippingRates.forEach(rate => {
            const shippingPriceRate = this.formatPrice(rate.estimatedCost?.amount);
            shippingData += `
                <li class="shipping-rate">
                    <div class="shipping-rate-title-wrapper d-flex justify-content-between align-items-start">
                       <div class="shipping-rate-title" >${this.escapeHtml(rate.title || '')}</div>
                       <div class="shipping-rate-price" >$${shippingPriceRate}</div>
                    </div>
                    <div class="shipping-rate-description" >${this.escapeHtml(rate.description || '')}</div>
                </li>`;
        });

        shippingData += `</ul>`;
        return shippingData;
    }

    async calculateCartShipping(zipcode) {
        zipcode = this.normalizeLookupPostcode(zipcode || '0');
        const zipcodeValue = parseInt(zipcode || '0', 10);
        let province = this.selectedLocation?.state || this.getProvinceCode(zipcode);
        let country = 'Australia';

        if (!zipcodeValue || !country || !province) return false;

        try {
            let response = await fetch(
                `/cart/shipping_rates.json?shipping_address[country]=${country}&shipping_address[province]=${province}&shipping_address[zip]=${encodeURIComponent(zipcode)}`
            );
            response = await response.json();

            let shippingRates = response.shipping_rates || [];
            if (shippingRates && shippingRates.length) {
                if (this.hasLocationUi) {
                    const locationInfo = this.selectedLocation
                        ? this.getDisplayLocation(this.selectedLocation)
                        : this.getDisplayLocation(null, zipcode);

                    let shippingData = `
                        
                        <ul class="shipping-rates d-flex flex-column">`;

                    shippingRates.forEach(shippingRate => {
                        let shippingPriceRate = this.formatPrice(shippingRate.price);
                        shippingData += `
                            <li class="shipping-rate">
                                <div class="shipping-rate-title-wrapper d-flex justify-content-between align-items-start">
                                   <div class="shipping-rate-title" >${this.escapeHtml(shippingRate.name || '')}</div>
                                   <div class="shipping-rate-price" >$${shippingPriceRate}</div>
                                </div>
                                <div class="shipping-rate-description" >${this.escapeHtml(shippingRate.description || '')}</div>
                            </li>`;
                    });

                    shippingData += `</ul>`;

                    if (this.shippingOptionsEl) {
                        this.shippingOptionsEl.innerHTML = shippingData;
                        this.shippingOptionsEl?.classList?.remove('d-none');
                    }

                    return true;
                }

                let shippingData = `
                            <div class="accordion">
                                <details id="Details-cart-custom-calculate-shipping" open>
                                    <summary class="align-items-center p-0">
                                        <div class="summary__title">
                                           <div class="shipping-rate-info">Shipping options to ${province}, ${zipcode}</div>
                                        </div>
                                        <span class="svg-wrapper">
                                            <svg viewBox="0 0 20 20" class="icon accordion-icon-open" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path id="icon-plus" d="M10 4.375v11.25M15.625 10H4.375" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
                                            </svg>
                                            <svg viewBox="0 0 20 20" class="icon accordion-icon-close" fill="none" xmlns="http://www.w3.org/2000/svg">
                                              <path id="icon-minus" d="M15.625 10H4.375" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"></path>
                                            </svg>
                                        </span>
                                    </summary>
                                    <ul class="shipping-rates d-flex flex-column">`;

                shippingRates.forEach(shippingRate => {
                    let shippingPriceRate = this.formatPrice(shippingRate.price);
                    shippingData += `                  
                        <li class="shipping-rate">
                            <div class="shipping-rate-title-wrapper d-flex justify-content-between align-items-start">
                               <div class="shipping-rate-title" >${shippingRate.name}</div>
                               <div class="shipping-rate-price" >$${shippingPriceRate}</div>
                            </div>
                            <div class="shipping-rate-description" >${shippingRate.description}</div>
                        </li>`;
                });

                shippingData += `</ul>
                               </details>
                            </div>`;

                if (this.shippingOptionsEl) {
                    this.shippingOptionsEl.innerHTML = shippingData;
                    this.shippingOptionsEl?.classList?.remove('d-none');
                }

                return true;
            }

            this.shippingOptionsEl?.classList?.add('d-none');
            return false;
        }
        catch (e) {
            console.error('Calculate shipping cart api failed => ', e);
            return false;
        }
    }

    getDefaultLocation() {
        const suburb = (this.dataset.defaultSuburb || '').trim();
        const state = (this.dataset.defaultState || '').trim().toUpperCase();
        const postcode = this.normalizeLookupPostcode(this.dataset.defaultPostcode || '');

        if (!suburb || !state || !postcode) return null;

        return {
            suburb,
            state,
            postcode
        };
    }

    getStoredLocation() {
        const cookieValue = this.getCookieValue(this.locationCookieKey);
        if (!cookieValue) return null;

        try {
            const parsedValue = JSON.parse(decodeURIComponent(cookieValue));
            if (!parsedValue?.postcode || !parsedValue?.state) return null;

            return {
                suburb: parsedValue.suburb || '',
                state: parsedValue.state,
                postcode: this.normalizeLookupPostcode(parsedValue.postcode)
            };
        } catch (error) {
            return null;
        }
    }

    setStoredLocation(location) {
        this.setCookieValue(
            this.locationCookieKey,
            encodeURIComponent(JSON.stringify(location)),
            31 * 24 * 3600 * 1000
        );
    }

    clearStoredLocation() {
        this.deleteCookie(this.locationCookieKey);
    }

    isValidRawPostcode(postcode) {
        return Boolean(postcode) && postcode.length === this.postcodeLength;
    }

    normalizePostcode(value) {
        return (value || '').replace(/\D/g, '').slice(0, this.postcodeLength);
    }

    normalizeSearchValue(value) {
        const normalizedValue = (value || '')
            .replace(/[^0-9A-Za-z\s'.-]/g, '')
            .replace(/\s{2,}/g, ' ')
            .trimStart()
            .slice(0, 50);

        return this.isNumericSearchValue(normalizedValue)
            ? normalizedValue.slice(0, this.postcodeLength)
            : normalizedValue;
    }

    normalizeLookupPostcode(value) {
        const normalizedValue = this.normalizePostcode(value);
        if (!normalizedValue) return '';

        if (!this.isAustralia || normalizedValue.length >= this.postcodeLength) return normalizedValue;

        const parsedValue = parseInt(normalizedValue, 10);
        return !Number.isNaN(parsedValue) && parsedValue >= 200
            ? normalizedValue.padStart(this.postcodeLength, '0')
            : normalizedValue;
    }

    getLocationKey(location) {
        return [location.suburb, location.state, location.postcode].join('|');
    }

    isNumericSearchValue(value) {
        return /^\d+$/.test((value || '').trim());
    }

    shouldSearchLocations(value) {
        const searchValue = (value || '').trim();
        if (!searchValue) return false;

        return this.isNumericSearchValue(searchValue)
            ? searchValue.length >= this.postcodeLength
            : CustomCalculateShipping.normalizeSearchText(searchValue).length >= 2;
    }

    isLocationMatchForSearch(location, value) {
        if (!location || !value) return false;

        const searchValue = value.trim();
        if (this.isNumericSearchValue(searchValue)) {
            return location.postcode === this.normalizeLookupPostcode(searchValue);
        }

        return CustomCalculateShipping.normalizeSearchText(location.suburb).includes(CustomCalculateShipping.normalizeSearchText(searchValue));
    }

    async findLocationsByPostcode(postcode) {
        if (!this.isAustralia || !this.postcodesUrl) return [];

        try {
            const postcodeIndex = await CustomCalculateShipping.loadPostcodeIndex(this.postcodesUrl);
            return postcodeIndex.get(this.normalizeLookupPostcode(postcode)) || [];
        } catch (error) {
            return null;
        }
    }

    async findLocationsBySearch(value) {
        const searchValue = this.normalizeSearchValue(value).trim();
        if (!searchValue) return [];

        if (this.isNumericSearchValue(searchValue)) {
            return this.findLocationsByPostcode(searchValue);
        }

        return this.findLocationsBySuburb(searchValue);
    }

    async findLocationsBySuburb(value) {
        if (!this.isAustralia || !this.postcodesUrl) return [];

        try {
            const postcodeIndex = await CustomCalculateShipping.loadPostcodeIndex(this.postcodesUrl);
            const allLocations = CustomCalculateShipping.getLocationsFromPostcodeIndex(postcodeIndex);
            const searchValue = CustomCalculateShipping.normalizeSearchText(value);
            const startsWithMatches = [];
            const containsMatches = [];

            allLocations.forEach((location) => {
                const suburb = CustomCalculateShipping.normalizeSearchText(location.suburb);
                if (suburb.startsWith(searchValue)) {
                    startsWithMatches.push(location);
                    return;
                }

                if (suburb.includes(searchValue)) {
                    containsMatches.push(location);
                }
            });

            return startsWithMatches.concat(containsMatches).slice(0, 12);
        } catch (error) {
            return null;
        }
    }

    static async loadPostcodeIndex(url) {
        if (!CustomCalculateShipping.postcodeIndexPromise) {
            CustomCalculateShipping.postcodeIndexPromise = fetch(url)
                .then((response) => {
                    if (!response.ok) {
                        throw new Error(`Failed to load postcode lookup data: ${response.status}`);
                    }

                    return response.text();
                })
                .then((csvText) => CustomCalculateShipping.parsePostcodeCsv(csvText));
        }

        return CustomCalculateShipping.postcodeIndexPromise;
    }

    static parsePostcodeCsv(csvText) {
        const postcodeIndex = new Map();
        const seenLocations = new Set();
        const rows = csvText.split(/\r?\n/).slice(1);

        rows.forEach((row) => {
            if (!row) return;

            const [rawSuburb, rawState, rawPostcode] = row.split(',');
            if (!rawSuburb || !rawState || !rawPostcode) return;

            const postcode = CustomCalculateShipping.normalizeCsvPostcode(rawPostcode);
            if (!postcode) return;

            const location = {
                suburb: CustomCalculateShipping.formatSuburb(rawSuburb),
                state: rawState.trim().toUpperCase(),
                postcode
            };
            const locationKey = `${location.suburb}|${location.state}|${location.postcode}`;

            if (seenLocations.has(locationKey)) return;
            seenLocations.add(locationKey);

            if (!postcodeIndex.has(postcode)) {
                postcodeIndex.set(postcode, []);
            }

            postcodeIndex.get(postcode).push(location);
        });

        postcodeIndex.forEach((locations) => {
            locations.sort((locationA, locationB) => locationA.suburb.localeCompare(locationB.suburb));
        });

        return postcodeIndex;
    }

    static getLocationsFromPostcodeIndex(postcodeIndex) {
        if (CustomCalculateShipping.postcodeLocationList) {
            return CustomCalculateShipping.postcodeLocationList;
        }

        const locations = [];

        postcodeIndex.forEach((postcodeLocations) => {
            locations.push(...postcodeLocations);
        });

        CustomCalculateShipping.postcodeLocationList = locations.sort((locationA, locationB) => {
            return locationA.suburb.localeCompare(locationB.suburb) || locationA.postcode.localeCompare(locationB.postcode);
        });

        return CustomCalculateShipping.postcodeLocationList;
    }

    static normalizeCsvPostcode(value) {
        const postcode = (value || '').trim().replace(/\D/g, '');
        if (!postcode) return '';

        return postcode.length < 4 ? postcode.padStart(4, '0') : postcode.slice(0, 4);
    }

    static formatSuburb(value) {
        return (value || '')
            .trim()
            .toLowerCase()
            .replace(/(^|[\s\-'])([a-z])/g, (match, prefix, character) => `${prefix}${character.toUpperCase()}`);
    }

    static normalizeSearchText(value) {
        return (value || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
    }

    setCookieValue(name, value, miliseconds = 1) {
        const date = new Date();
        date.setTime(date.getTime() + miliseconds);
        const expires = `expires=${date.toUTCString()}`;
        document.cookie = `${name}=${value};${expires};path=/`;
    }

    deleteCookie(name) {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    }

    getCookieValue(name) {
        const cookieArr = document.cookie.split(';');
        for (let i = 0; i < cookieArr.length; i += 1) {
            const cookie = cookieArr[i].trim();
            if (cookie.indexOf(`${name}=`) === 0) {
                return cookie.substring((`${name}=`).length, cookie.length);
            }
        }
        return null;
    }

    getLocalStorageValue(name) {
        try {
            return window.localStorage.getItem(name);
        } catch (error) {
            return null;
        }
    }

    setLocalStorageValue(name, value) {
        try {
            window.localStorage.setItem(name, value);
        } catch (error) {}
    }

    formatPrice(amount) {
        const parsedAmount = parseFloat(amount || '0');
        return Number.isNaN(parsedAmount) ? '0.00' : parsedAmount.toFixed(2);
    }

    escapeHtml(value) {
        return String(value)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
    }

    escapeAttribute(value) {
        return this.escapeHtml(value);
    }
}

customElements.define('custom-calculate-shipping', CustomCalculateShipping);

class CustomLoadMore extends HTMLElement {
    constructor() {
        super();

        this.sectionId = this.getAttribute('data-section-id');
        this.nextPageUrl = null;
        this.bindEvents();
    }

    bindEvents() {
        let loadMoreEl = this.querySelector('[data-load-more-button]');
        if (!loadMoreEl) return;

        this.nextPageUrl = loadMoreEl.getAttribute('href');
        loadMoreEl.addEventListener('click', (event) => this.handleLoadMore(event, loadMoreEl))
    }

    async handleLoadMore(event, loadMoreEl) {

        event.preventDefault();
        event.stopPropagation();

        let urlAttributes = new URLSearchParams(window.location.search);

        try {
            let loadMoreLoadingEl = this.querySelector('[data-custom-load-more-loader]');
            loadMoreLoadingEl?.classList?.remove('d-none');
            loadMoreEl?.classList?.add('custom-loading');

            let splitNextPageUrl = this.nextPageUrl.split('?');
            let url = splitNextPageUrl[0] || '';
            let nextPageQuery = splitNextPageUrl[1] || '';

            let nextPageSearchParams = new URLSearchParams(nextPageQuery);
            let nextPage = nextPageSearchParams.get('page');

            urlAttributes.set('page', nextPage);

            let collectionProducts = await fetch(`${url}?sections=${this.sectionId}&${urlAttributes.toString()}`);
            collectionProducts = await collectionProducts.json();

            let gridSelector = `ul#product-grid[data-id=${this.sectionId}]`;

            let collectionProductGridSectionEl = (new DOMParser()).parseFromString(collectionProducts[this.sectionId], 'text/html');
            let newCollectionProductGridSectionEl = collectionProductGridSectionEl.querySelector(gridSelector);
            if (!newCollectionProductGridSectionEl) return

            let existingProductSectionEl = document.querySelector(gridSelector);
            existingProductSectionEl.innerHTML += newCollectionProductGridSectionEl.innerHTML;

            let existingLoadMoreInfoEl = document.querySelector('[data-paginate-info]');
            let newLoadMoreInfoEl = collectionProductGridSectionEl.querySelector('[data-paginate-info]');
            if (newLoadMoreInfoEl) {
                existingLoadMoreInfoEl.innerHTML = newLoadMoreInfoEl.innerHTML;
            }

            let existingLoadMoreEl = document.querySelector('custom-load-more');
            let newLoadMoreEl = collectionProductGridSectionEl.querySelector('custom-load-more');

            if (newLoadMoreEl) {
                existingLoadMoreEl.outerHTML = newLoadMoreEl.outerHTML;
            }
            else {
                existingLoadMoreEl.remove();
            }

            loadMoreLoadingEl?.classList?.add('d-none');
            loadMoreEl?.classList?.remove('custom-loading');
        }
        catch (e) {
            console.error('Failed to fetch collection products', e);
        }
    }
}

customElements.define('custom-load-more', CustomLoadMore);

class AccordionToggle extends HTMLElement {
    constructor() {
        super();
        this.accordionDetails = this.querySelector('details');
        this.allowMultiple = this.hasAttribute('data-accordion-allow-multiple');
        this.bindEvents();
    }

    bindEvents() {
        if (!this.accordionDetails) return;

        this.accordionDetails.addEventListener('click', (event) => {
            event.stopPropagation();
            if (this.allowMultiple) return;

            this.closeOtherAccordions();
        });
    }

    closeOtherAccordions() {
        const allAccordions = document.querySelectorAll('accordion-toggle details[open]');
        allAccordions.forEach((details) => {
            const accordionToggle = details.closest('accordion-toggle');
            const allowsMultiple = accordionToggle?.hasAttribute('data-accordion-allow-multiple');

            if (details !== this.accordionDetails && !allowsMultiple) {
                details.removeAttribute('open');
            }
        });
    }
}

document.addEventListener('click', (event) => {
    document.querySelectorAll('accordion-toggle').forEach(accordionToggleEl => {
        if (accordionToggleEl.hasAttribute('data-accordion-allow-multiple')) return;

        let accordionDetailsEl = accordionToggleEl.querySelector('details');
        if (accordionToggleEl && accordionDetailsEl && !accordionToggleEl.contains(event.target)) {
            accordionDetailsEl.removeAttribute('open');
        }
    })
});

customElements.define('accordion-toggle', AccordionToggle);
